import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  ControlButton,
  MiniMap,
  Panel,
  ReactFlowProvider,
  Node,
  Edge,
  useReactFlow,
  useUpdateNodeInternals,
  ConnectionLineType,
  SelectionMode,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ArrowLeft, Play, Save, Settings, Share2, X, Zap, Maximize2, Sparkles } from 'lucide-react';
import { Panel as ResizablePanel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { workflowsApi, executionsApi } from '@/lib/api';
import { workflowApi } from '@/lib/api';
import { validationApi } from '@/lib/validation';
import { useWorkflowStore } from '@/stores/workflow-store';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import EnhancedNodePalette from '@/components/workflow/EnhancedNodePalette';

import { LiveExecutionPanel, LiveExecutionProvider } from '@/components/workflow/live-execution';
import ShareModal from '@/components/workflow/ShareModal';
import { nodeTypes } from '@/components/workflow/nodes';
import { CustomEdge } from '@/components/workflow/edges/CustomEdge';
import { SmartEdge } from '@/components/workflow/edges/SmartEdge';
import { ExecutionProgressBar } from '@/components/workflow/ExecutionProgressBar';
import { GlobalProgressBar } from '@/components/ui/global-progress-bar';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { WorkflowEditorSkeleton } from '@/components/ui/workflow-skeleton';
import { useExecutionProgress } from '@/hooks/use-execution-progress';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { CanvasContextMenu } from '@/components/workflow/CanvasContextMenu';
import { EdgeContextMenuPortal } from '@/components/workflow/EdgeContextMenuPortal';
import { nodeDefinitions, getNodeDefinition } from '@/utils/nodeDefinitions';
import { ALL_NODE_DEFINITIONS } from '@shared/nodes';
import { getNodeSchema, getNodeDefaults } from '@/lib/node-registry';
import { useCanvasOperations } from '@/hooks/useCanvasOperations';
// import NodePropertiesPanel from '@/components/workflow/NodePropertiesPanel';
import { NodeEditorModal } from '@/components/workflow/node-editor/NodeEditorModal';
import { KeyboardShortcutsDialog } from '@/components/workflow/KeyboardShortcutsDialog';
import { ManualPasteProvider, useManualPaste } from '@/contexts/ManualPasteContext';
import { ManualPasteDialog } from '@/components/workflow/ManualPasteDialog';
import { N8nImportDialog } from '@/components/workflow/N8nImportDialog';
import type { TransformResult } from '@/lib/n8n-import';
import { EditableWorkflowHeader } from '@/components/workflow/EditableWorkflowHeader';
import { ValidationErrors } from '@/components/workflow/ValidationErrors';
import { useSocket } from '@/contexts/RealTimeContext';
import { WorkflowProvider } from '@/contexts/WorkflowContext';
import { migrateAllNodes, needsMigration } from '@/lib/workflow-migration';
import { generateUniqueNodeLabel, getNodeLabels } from '@/utils/node-label-utils';
import { ChatTestPanel } from '@/components/workflow/ChatTestPanel';
import { WorkflowAssistantPanel } from '@/components/workflow/workflow-assistant';
import type { WorkflowPatch } from '@/components/workflow/workflow-assistant/types';
import { validateEdges } from '@/utils/edge-validation';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { WorkflowErrorBoundary } from '@/components/error/WorkflowErrorBoundary';
import { UnsavedChangesDialog } from '@/components/workflow/UnsavedChangesDialog';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import { useFeatureToggle } from '@/hooks/useFeatureToggle';

// Define edgeTypes outside component to prevent React Flow warning
// nodeTypes is already defined in the nodes/index.ts file
const edgeTypes = {
  custom: SmartEdge,
};

function WorkflowEditorInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [workflow, setWorkflow] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isInsertingNode, setIsInsertingNode] = useState(false);
  const [insertEdgeInfo, setInsertEdgeInfo] = useState<{
    edgeId: string;
    sourceId: string;
    targetId: string;
    midpoint: { x: number; y: number };
  } | null>(null);
  const [isExecutionPanelOpen, setIsExecutionPanelOpen] = useState(false);
  const [paletteForceCloseCounter, setPaletteForceCloseCounter] = useState(0);
  const [currentExecution, setCurrentExecution] = useState<{
    id: string;
    workflow_id: string;
    status: 'running' | 'success' | 'failed' | 'cancelled';
    trigger_type: string;
    created_at: number;
    completed_at?: number;
    error?: string;
  } | null>(null);
  const [isNodeEditorOpen, setIsNodeEditorOpen] = useState(false);
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const [paletteCategory, setPaletteCategory] = useState<string | undefined>(undefined);
  const [addingAfterNodeId, setAddingAfterNodeId] = useState<string | null>(null);
  const [addingAfterHandle, setAddingAfterHandle] = useState<string | undefined>(undefined);
  const [subNodeFilter, setSubNodeFilter] = useState<'chatModel' | 'memory' | 'tools' | 'tool' | 'parser' | 'pollSource' | undefined>(undefined);
  const [visiblePaletteCategories, setVisiblePaletteCategories] = useState<string[] | undefined>(undefined);
  const [anchorMenuState, setAnchorMenuState] = useState<{
    visible: boolean;
    anchorType: 'chatModel' | 'memory' | 'tools' | 'tool' | 'parser' | 'pollSource';
    position: { x: number; y: number };
    sourceNodeId: string;
    sourceHandle: string;
  } | null>(null);
  const [isChatTestOpen, setIsChatTestOpen] = useState(false);
  const [selectedChatTriggerId, setSelectedChatTriggerId] = useState<string | null>(null);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const { checkFlag } = useFeatureToggle();
  const isWorkflowAssistantEnabled = checkFlag('workflow-assistant', false, { userId: '' });
  const [nodeExecutionStatuses, setNodeExecutionStatuses] = useState<Record<string, 'idle' | 'running' | 'success' | 'failed'>>({});
  const [edgeExecutionStatuses, setEdgeExecutionStatuses] = useState<Record<string, 'idle' | 'active' | 'traversed'>>({});
  const [executionProgress, setExecutionProgress] = useState({
    status: 'idle' as 'idle' | 'running' | 'success' | 'failed',
    progress: 0,
    currentNode: undefined as string | undefined,
    totalNodes: 0,
    completedNodes: 0,
    // Loop-aware progress tracking
    completedUniqueNodes: new Set<string>(),
    loopIterations: {} as Record<string, number>, // nodeId -> iteration count
    expectedTotalExecutions: 0, // Dynamic total including loop iterations
    hasLoops: false, // Whether workflow contains loop nodes
    isFullWorkflowExecution: false, // Track if this is a full workflow execution (not test/execute-up-to)
  });
  
  // Ref to track current execution ID to avoid stale closures in event handlers
  const currentExecutionIdRef = useRef<string | null>(null); // Fixed: Added useRef import

  // Sync ref with currentExecution state
  useEffect(() => {
    currentExecutionIdRef.current = currentExecution?.id || null;
  }, [currentExecution?.id]);
  
  const [showShortcutsDialog, setShowShortcutsDialog] = useState(false);
  const [executionStore, setExecutionStore] = useState<Record<string, any>>({});
  const [isLoadingExecutionStore, setIsLoadingExecutionStore] = useState(false);
  const [lastExecutionId, setLastExecutionId] = useState<string | null>(null);
  const [canvasMenuOpen, setCanvasMenuOpen] = useState(false);
  const [canvasMenuPosition, setCanvasMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [edgeMenuOpen, setEdgeMenuOpen] = useState(false);
  const [edgeMenuPosition, setEdgeMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [contextMenuEdgeId, setContextMenuEdgeId] = useState<string | null>(null);
  const [versionRefreshTrigger, setVersionRefreshTrigger] = useState(0);
  const [validationErrors, setValidationErrors] = useState<any[]>([]);
  const [validationWarnings, setValidationWarnings] = useState<any[]>([]);
  const [currentVersionNumber, setCurrentVersionNumber] = useState<number | null>(null);
  const { connection: socket, isConnected, subscribeToExecution, unsubscribeFromExecution } = useSocket();
  
  // Get ReactFlow instance for fitView
  const { fitView: reactFlowFitView, setCenter, setViewport, getViewport } = useReactFlow();
  
  // Get updateNodeInternals hook for forcing handle position recalculation
  const updateNodeInternals = useUpdateNodeInternals();

  // Use selectors to prevent unnecessary re-renders - MUST be before useEffect hooks that use them
  const nodes = useWorkflowStore((state) => state.nodes);
  const edges = useWorkflowStore((state) => state.edges);
  const selectedNode = useWorkflowStore((state) => state.selectedNode);
  const selectedEdge = useWorkflowStore((state) => state.selectedEdge);
  const setNodes = useWorkflowStore((state) => state.setNodes);
  const setEdges = useWorkflowStore((state) => state.setEdges);
  const storeOnNodesChange = useWorkflowStore((state) => state.onNodesChange);
  const storeOnEdgesChange = useWorkflowStore((state) => state.onEdgesChange);
  const storeOnConnect = useWorkflowStore((state) => state.onConnect);
  const setSelectedNode = useWorkflowStore((state) => state.setSelectedNode);
  const selectEdge = useWorkflowStore((state) => state.selectEdge);
  const clearWorkflow = useWorkflowStore((state) => state.clearWorkflow);
  const removeEdge = useWorkflowStore((state) => state.removeEdge);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addNode = useWorkflowStore((state) => state.addNode);
  const setCurrentWorkflowMetadata = useWorkflowStore((state) => state.setCurrentWorkflowMetadata);
  const highlightedNodeId = useWorkflowStore((state) => state.highlightedNodeId);
  const setHighlightedNodeId = useWorkflowStore((state) => state.setHighlightedNodeId);
  const highlightedEdgeId = useWorkflowStore((state) => state.highlightedEdgeId);
  const setHighlightedEdgeId = useWorkflowStore((state) => state.setHighlightedEdgeId);

  // Ref to track the node type being dragged from palette (for drop-on-node feature)
  const draggingNodeTypeRef = useRef<string | null>(null);

  // Ref to track when an existing canvas node is being dragged
  const draggingCanvasNodeRef = useRef<{ id: string; type: string } | null>(null);

  // Ref to track edges for edge execution status updates (avoid stale closures)
  const edgesRef = useRef(edges);

  // Sync edges ref with edges state
  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  // Track saved state for unsaved changes detection
  const savedStateRef = useRef<{ nodes: string; edges: string } | null>(null);
  const [savedVersion, setSavedVersion] = useState(0); // Increments on save to trigger re-computation
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // Compute if there are unsaved changes by comparing current state to saved state
  const hasUnsavedChanges = useMemo(() => {
    // savedVersion is included to trigger re-computation after save
    void savedVersion;
    if (!savedStateRef.current || loading) return false;

    // Strip transient data before comparison (same as handleSave does)
    const cleanNodes = nodes.map(node => {
      const { executionOutput, cachedInputData, ...cleanData } = node.data || {};
      return { ...node, data: cleanData };
    });

    const currentNodesStr = JSON.stringify(cleanNodes);
    const currentEdgesStr = JSON.stringify(edges);

    return currentNodesStr !== savedStateRef.current.nodes ||
           currentEdgesStr !== savedStateRef.current.edges;
  }, [nodes, edges, loading, savedVersion]);

  // Navigation guard for unsaved changes
  const { isBlocked, proceed, cancel, guardedNavigate, pendingPath } = useNavigationGuard({
    hasUnsavedChanges,
    enabled: !loading && !!workflow,
  });

  // Show dialog when navigation is blocked
  useEffect(() => {
    if (isBlocked) {
      setShowUnsavedDialog(true);
    }
  }, [isBlocked]);

  // Close edge context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (edgeMenuOpen) {
        setEdgeMenuOpen(false);
      }
    };

    if (edgeMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [edgeMenuOpen]);

  // Reset paletteCategory after it's been used
  useEffect(() => {
    if (paletteCategory) {
      const timer = setTimeout(() => {
        setPaletteCategory(undefined);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [paletteCategory]);

  // Listen for palette drag events to track what node type is being dragged (for drop-on-node feature)
  useEffect(() => {
    const handlePaletteDragStart = (e: Event) => {
      const customEvent = e as CustomEvent<{ nodeType: string }>;
      draggingNodeTypeRef.current = customEvent.detail.nodeType;
    };
    const handlePaletteDragEnd = () => {
      draggingNodeTypeRef.current = null;
      setHighlightedNodeId(null);
      setHighlightedEdgeId(null);
    };

    window.addEventListener('nodepalette:dragstart', handlePaletteDragStart);
    window.addEventListener('nodepalette:dragend', handlePaletteDragEnd);

    return () => {
      window.removeEventListener('nodepalette:dragstart', handlePaletteDragStart);
      window.removeEventListener('nodepalette:dragend', handlePaletteDragEnd);
    };
  }, [setHighlightedNodeId, setHighlightedEdgeId]);

  // Load latest execution store on mount (for persisting outputs across page refreshes)
  // Note: updateNodesWithExecutionOutputs is defined later after useWorkflowStore hook

  // Listen for execution completion events to update currentExecution status
  useEffect(() => {
    const handleExecutionCompleted = async (event: Event) => {
      const data = (event as CustomEvent).detail;
      const currentExecId = currentExecutionIdRef.current;
      
      if (!currentExecId || data.executionId !== currentExecId) {
        return;
      }
      
      setCurrentExecution(prev => prev ? {
        ...prev,
        status: 'success',
        completed_at: Date.now(),
      } : null);

      // Update execution progress to 100%
      setExecutionProgress(prev => ({
        ...prev,
        status: 'success',
        progress: 100,
      }));

      // Clear all node/edge execution statuses after a short delay
      // This allows the success animations to complete before clearing
      setTimeout(() => {
        setNodeExecutionStatuses({});
        setEdgeExecutionStatuses({});
      }, 2000);
    };

    const handleExecutionFailed = (event: Event) => {
      const data = (event as CustomEvent).detail;
      const currentExecId = currentExecutionIdRef.current;
      
      if (!currentExecId || data.executionId !== currentExecId) {
        return;
      }
      
      setCurrentExecution(prev => prev ? {
        ...prev,
        status: 'failed',
        completed_at: Date.now(),
        error: data.error,
      } : null);

      // Update execution progress status
      setExecutionProgress(prev => ({
        ...prev,
        status: 'failed',
      }));

      // Clear all node execution statuses to stop any running animations
      // This is important for sub-nodes that may not receive their own failed event
      setNodeExecutionStatuses({});
      setEdgeExecutionStatuses({});
    };

    const handleExecutionCancelled = (event: Event) => {
      const data = (event as CustomEvent).detail;
      const currentExecId = currentExecutionIdRef.current;
      
      if (!currentExecId || data.executionId !== currentExecId) {
        return;
      }
      
      setCurrentExecution(prev => prev ? {
        ...prev,
        status: 'cancelled',
        completed_at: Date.now(),
      } : null);

      // Clear all node execution statuses to stop any running animations
      setNodeExecutionStatuses({});
      setEdgeExecutionStatuses({});
    };

    // Listen to window custom events dispatched by RealTimeContext
    window.addEventListener('execution:completed', handleExecutionCompleted);
    window.addEventListener('execution:failed', handleExecutionFailed);
    window.addEventListener('execution:cancelled', handleExecutionCancelled);

    return () => {
      window.removeEventListener('execution:completed', handleExecutionCompleted);
      window.removeEventListener('execution:failed', handleExecutionFailed);
      window.removeEventListener('execution:cancelled', handleExecutionCancelled);
    };
  }, []); // Empty dependency array - use ref instead

  // Listen for execution:started events to auto-select new executions
  useEffect(() => {
    const handleExecutionStarted = (event: Event) => {
      const data = (event as CustomEvent).detail;
      
      // Only auto-select if this execution is for the current workflow
      if (data.workflowId === workflow?.id) {
        
        // Check if this execution is already set (to avoid duplicate updates)
        if (currentExecution?.id === data.executionId) {
          return;
        }
        
        // Check if this is a full workflow execution (not test/execute-up-to)
        const isFullWorkflow = data.mode === 'FullWorkflow' || data.mode === 'Production';

        // Only track full workflow executions in the LiveExecutionPanel
        // Test node and execute-up-to operations run silently without panel tracking
        if (isFullWorkflow) {
          // Set as current execution for live tracking
          setCurrentExecution({
            id: data.executionId,
            workflow_id: data.workflowId,
            status: data.status || 'running',
            trigger_type: data.triggerType || 'unknown',
            created_at: Date.now(),
          });

          // Subscribe to execution updates
          subscribeToExecution(data.executionId);

          // Open execution panel for full workflow runs
          setIsExecutionPanelOpen(true);

          // Initialize execution progress
          setExecutionProgress({
            status: 'running',
            progress: 0,
            currentNode: undefined,
            totalNodes: nodes.length,
            completedNodes: 0,
            completedUniqueNodes: new Set<string>(),
            loopIterations: {},
            expectedTotalExecutions: nodes.length,
            hasLoops: false,
            isFullWorkflowExecution: true,
          });

          // Reset edge execution statuses when new execution starts
          setEdgeExecutionStatuses({});
        }
      }
    };

    window.addEventListener('execution:started', handleExecutionStarted);

    return () => {
      window.removeEventListener('execution:started', handleExecutionStarted);
    };
  }, [workflow?.id, subscribeToExecution, currentExecution?.id]);

  // Poll execution status when running (fallback for missed WebSocket events)
  useEffect(() => {
    if (!currentExecution || currentExecution.status !== 'running') return;

    let pollCount = 0;
    const maxPolls = 60; // Stop after 60 polls (2 minutes)
    
    const pollInterval = setInterval(async () => {
      pollCount++;
      
      // Stop polling after max attempts
      if (pollCount > maxPolls) {
        clearInterval(pollInterval);
        return;
      }
      
      try {
        const response = await executionsApi.getExecutionState(currentExecution.id);
        const state = response.data;
        
        // Update execution status if it has changed
        if (state.status !== 'running' && state.status !== currentExecution.status) {
          setCurrentExecution(prev => prev ? {
            ...prev,
            status: state.status,
            completed_at: state.completedAt || Date.now(),
            error: state.error,
          } : null);
          
          // Update execution progress to 100% when completed successfully
          if (state.status === 'success') {
            setExecutionProgress(prev => ({
              ...prev,
              status: 'success',
              progress: 100,
            }));
          } else if (state.status === 'failed') {
            setExecutionProgress(prev => ({
              ...prev,
              status: 'failed',
            }));
          }
          
          // Unsubscribe from execution updates
          unsubscribeFromExecution(currentExecution.id);
          
          // Stop polling once execution is complete
          clearInterval(pollInterval);
        }
      } catch (error) {
        // Silently handle errors - execution might not be in DB yet
      }
    }, 2000); // Poll every 2 seconds (reduced from 1 second)

    return () => clearInterval(pollInterval);
  }, [currentExecution, socket]);

  // Poll node execution statuses when running (fallback for missed WebSocket events)
  useEffect(() => {
    if (!currentExecution || currentExecution.status !== 'running') {
      // When execution completes/fails, ensure node statuses are cleared
      // This prevents polling from restoring stale 'running' statuses
      if (currentExecution && (currentExecution.status === 'success' || currentExecution.status === 'failed' || currentExecution.status === 'cancelled')) {
        setNodeExecutionStatuses({});
        setEdgeExecutionStatuses({});
      }
      return;
    }

    let pollCount = 0;
    const maxPolls = 60; // Stop after 60 polls (2 minutes)
    
    const pollInterval = setInterval(async () => {
      pollCount++;
      
      // Stop polling after max attempts
      if (pollCount > maxPolls) {
        clearInterval(pollInterval);
        return;
      }
      
      try {
        const response = await executionsApi.getNodeExecutions(currentExecution.id);
        // Handle both paginated response { items: [...] } and legacy array response
        const nodeExecs = response.data?.items ?? response.data ?? [];

        // Update node execution statuses
        const newStatuses: Record<string, 'idle' | 'running' | 'success' | 'failed'> = {};
        (Array.isArray(nodeExecs) ? nodeExecs : []).forEach((nodeExec: any) => {
          if (nodeExec.status === 'success') {
            newStatuses[nodeExec.nodeId || nodeExec.node_id] = 'success';
          } else if (nodeExec.status === 'failed') {
            newStatuses[nodeExec.nodeId || nodeExec.node_id] = 'failed';
          } else if (nodeExec.status === 'running') {
            newStatuses[nodeExec.nodeId || nodeExec.node_id] = 'running';
          }
        });
        
        setNodeExecutionStatuses(newStatuses);
      } catch (error) {
        // Silently handle errors - node executions might not be in DB yet
      }
    }, 2000); // Poll every 2 seconds (reduced from 1 second)

    return () => {
      clearInterval(pollInterval);
    };
  }, [currentExecution]);

  // Listen for node execution events to update node statuses in real-time
  useEffect(() => {
    const handleNodeStarted = (event: Event) => {
      const data = (event as CustomEvent).detail;
      const currentExecId = currentExecutionIdRef.current;
      if (!currentExecId || data.executionId !== currentExecId) return;

      setNodeExecutionStatuses(prev => ({
        ...prev,
        [data.nodeId]: 'running'
      }));

      // Mark incoming edges as "active" (data is flowing through them)
      setEdgeExecutionStatuses(prev => {
        const newStatuses = { ...prev };
        const currentEdges = edgesRef.current;
        currentEdges.forEach(edge => {
          if (edge.target === data.nodeId) {
            newStatuses[edge.id] = 'active';
          }
        });
        return newStatuses;
      });
    };

    const handleNodeCompleted = (event: Event) => {
      const data = (event as CustomEvent).detail;
      const currentExecId = currentExecutionIdRef.current;
      if (!currentExecId || data.executionId !== currentExecId) return;

      setNodeExecutionStatuses(prev => ({
        ...prev,
        [data.nodeId]: 'success'
      }));

      // Mark incoming edges as "traversed" (completed) and outgoing edges as "active"
      // For conditional nodes (If/Switch), only activate edges that match the branch output
      setEdgeExecutionStatuses(prev => {
        const newStatuses = { ...prev };
        const currentEdges = edgesRef.current;
        const output = data.output;

        currentEdges.forEach(edge => {
          if (edge.target === data.nodeId) {
            // Incoming edge is now traversed
            newStatuses[edge.id] = 'traversed';
          } else if (edge.source === data.nodeId) {
            // For conditional nodes (If/Switch), only activate the matching branch edge
            if (output?.branch !== undefined) {
              // If node: only activate edge where sourceHandle matches branch
              const branchStr = String(output.branch).toLowerCase();
              const handleStr = (edge.sourceHandle || '').toLowerCase();
              if (handleStr === branchStr) {
                newStatuses[edge.id] = 'active';
              }
              // Don't activate non-matching edges
            } else if (output?.matchedOutput !== undefined) {
              // Switch node: activate edges that match the selected outputs
              const matchedOutputs = String(output.matchedOutput).split(',').map((s: string) => s.trim().toLowerCase());
              const handleStr = (edge.sourceHandle || '').toLowerCase();
              if (matchedOutputs.includes(handleStr)) {
                newStatuses[edge.id] = 'active';
              }
              // Don't activate non-matching edges
            } else {
              // Regular node: activate all outgoing edges
              newStatuses[edge.id] = 'active';
            }
          }
        });
        return newStatuses;
      });
      
      // Update execution progress with loop-aware tracking
      setExecutionProgress(prev => {
        const newCompletedNodes = prev.completedNodes + 1;
        const newCompletedUniqueNodes = new Set(prev.completedUniqueNodes);
        newCompletedUniqueNodes.add(data.nodeId);
        
        // Calculate progress based on mode
        let progress = 0;
        if (prev.hasLoops && prev.expectedTotalExecutions > 0) {
          // Loop mode: Use dynamic total
          progress = (newCompletedNodes / prev.expectedTotalExecutions) * 100;
        } else {
          // Simple mode: Use unique nodes
          progress = prev.totalNodes > 0 ? (newCompletedUniqueNodes.size / prev.totalNodes) * 100 : 0;
        }
        
        return {
          ...prev,
          completedNodes: newCompletedNodes,
          completedUniqueNodes: newCompletedUniqueNodes,
          progress: Math.min(progress, 100), // Cap at 100%
        };
      });
      
      // Clear the status after a delay to stop animations
      setTimeout(() => {
        setNodeExecutionStatuses(prev => {
          const newStatuses = { ...prev };
          delete newStatuses[data.nodeId];
          return newStatuses;
        });
      }, 2000); // Increased to 2 seconds for better visibility
    };

    const handleNodeFailed = (event: Event) => {
      const data = (event as CustomEvent).detail;
      const currentExecId = currentExecutionIdRef.current;
      if (!currentExecId || data.executionId !== currentExecId) return;
      
      setNodeExecutionStatuses(prev => ({
        ...prev,
        [data.nodeId]: 'failed'
      }));
    };

    // Loop iteration handlers for loop-aware progress tracking
    const handleLoopIterationStarted = (event: Event) => {
      const data = (event as CustomEvent).detail;
      const currentExecId = currentExecutionIdRef.current;
      if (!currentExecId || data.executionId !== currentExecId) return;
      
      setExecutionProgress(prev => {
        const newLoopIterations = { ...prev.loopIterations };
        newLoopIterations[data.nodeId] = (newLoopIterations[data.nodeId] || 0) + 1;
        
        // Update expected total if we have iteration count
        let newExpectedTotal = prev.expectedTotalExecutions;
        if (data.totalIterations && data.totalIterations > 0) {
          // Calculate expected total based on loop iterations
          // This is a simplified calculation - real implementation would need to track all loops
          newExpectedTotal = prev.totalNodes + (data.totalIterations - 1);
        }
        
        return {
          ...prev,
          loopIterations: newLoopIterations,
          expectedTotalExecutions: newExpectedTotal,
          hasLoops: true,
        };
      });
    };

    const handleLoopIterationCompleted = (event: Event) => {
      const data = (event as CustomEvent).detail;
      const currentExecId = currentExecutionIdRef.current;
      if (!currentExecId || data.executionId !== currentExecId) return;
      
      // Loop completion is tracked through node:completed events
      // This event is mainly for logging and debugging
    };

    // Sub-node (tool execution) handlers for canvas animation
    const handleSubNodeStarted = (event: Event) => {
      const data = (event as CustomEvent).detail;
      const currentExecId = currentExecutionIdRef.current;

      // Debug: Log all subnode:started events
      console.log('[SubNode] Started event received:', {
        toolName: data.toolName,
        toolNodeId: data.toolNodeId,
        toolNodeType: data.toolNodeType,
        parentNodeId: data.parentNodeId,
        executionId: data.executionId,
        currentExecId
      });

      if (!currentExecId || data.executionId !== currentExecId) return;

      // Animate the tool node on the canvas (toolNodeId is the canvas node ID)
      if (data.toolNodeId) {
        console.log('[SubNode] Setting node status to running:', data.toolNodeId);
        setNodeExecutionStatuses(prev => ({
          ...prev,
          [data.toolNodeId]: 'running'
        }));
      } else {
        console.warn('[SubNode] No toolNodeId in event data!');
      }
    };

    const handleSubNodeCompleted = (event: Event) => {
      const data = (event as CustomEvent).detail;
      const currentExecId = currentExecutionIdRef.current;
      if (!currentExecId || data.executionId !== currentExecId) return;

      // Mark the tool node as success on the canvas
      if (data.toolNodeId) {
        setNodeExecutionStatuses(prev => ({
          ...prev,
          [data.toolNodeId]: 'success'
        }));

        // Clear the status after a delay to stop animations
        setTimeout(() => {
          setNodeExecutionStatuses(prev => {
            const newStatuses = { ...prev };
            delete newStatuses[data.toolNodeId];
            return newStatuses;
          });
        }, 2000);
      }
    };

    const handleSubNodeFailed = (event: Event) => {
      const data = (event as CustomEvent).detail;
      const currentExecId = currentExecutionIdRef.current;
      if (!currentExecId || data.executionId !== currentExecId) return;

      // Mark the tool node as failed on the canvas
      if (data.toolNodeId) {
        setNodeExecutionStatuses(prev => ({
          ...prev,
          [data.toolNodeId]: 'failed'
        }));
      }
    };

    window.addEventListener('node:started', handleNodeStarted);
    window.addEventListener('node:completed', handleNodeCompleted);
    window.addEventListener('node:failed', handleNodeFailed);
    window.addEventListener('loop:iteration', handleLoopIterationStarted);
    window.addEventListener('subnode:started', handleSubNodeStarted);
    window.addEventListener('subnode:completed', handleSubNodeCompleted);
    window.addEventListener('subnode:failed', handleSubNodeFailed);

    return () => {
      window.removeEventListener('node:started', handleNodeStarted);
      window.removeEventListener('node:completed', handleNodeCompleted);
      window.removeEventListener('node:failed', handleNodeFailed);
      window.removeEventListener('loop:iteration', handleLoopIterationStarted);
      window.removeEventListener('subnode:started', handleSubNodeStarted);
      window.removeEventListener('subnode:completed', handleSubNodeCompleted);
      window.removeEventListener('subnode:failed', handleSubNodeFailed);
    };
  }, []); // Removed socket dependency - using window events instead

  // Reset node execution statuses when execution starts
  useEffect(() => {
    if (currentExecution?.status === 'running') {
      setNodeExecutionStatuses({});
    }
  }, [currentExecution?.id]);

  // Callbacks for ReactFlow
  const onNodesChange = useCallback((changes: any[]) => {
    storeOnNodesChange(changes);
  }, [storeOnNodesChange]);
  
  const onEdgesChange = useCallback((changes: any[]) => {
    storeOnEdgesChange(changes);
  }, [storeOnEdgesChange]);

  // Canvas operations for copy/paste/duplicate
  const {
    copyNodes,
    cutNodes,
    pasteNodes,
    duplicateNodes,
    deleteSelectedNodes,
    selectAllNodes,
    deselectAllNodes,
    n8nImportState,
    closeN8nImportDialog,
  } = useCanvasOperations();

  // Handle execution state recovery on reconnection
  useEffect(() => {
    const handleStateRecovered = (event: CustomEvent) => {
      const { execution, nodeExecutions, logs } = event.detail;
      
      
      // Restore execution state
      if (execution) {
        setCurrentExecution({
          id: execution.executionId,
          workflow_id: execution.workflowId,
          status: execution.status,
          trigger_type: execution.triggerType,
          created_at: new Date(execution.startedAt).getTime(),
          completed_at: execution.completedAt ? new Date(execution.completedAt).getTime() : undefined,
          error: execution.error,
        });
        
        // Restore node execution statuses
        const nodeStatuses: Record<string, 'idle' | 'running' | 'success' | 'failed'> = {};
        let completedCount = 0;
        let runningCount = 0;
        
        nodeExecutions.forEach((nodeExec: any) => {
          if (nodeExec.status === 'completed') {
            nodeStatuses[nodeExec.nodeId] = 'success';
            completedCount++;
          } else if (nodeExec.status === 'failed') {
            nodeStatuses[nodeExec.nodeId] = 'failed';
          } else if (nodeExec.status === 'running') {
            nodeStatuses[nodeExec.nodeId] = 'running';
            runningCount++;
          }
        });
        
        setNodeExecutionStatuses(nodeStatuses);
        
        // Restore execution progress
        const totalNodes = nodes.length;
        const completedUniqueNodes = new Set<string>(
          nodeExecutions
            .filter((ne: any) => ne.status === 'completed')
            .map((ne: any) => ne.nodeId as string)
        );
        
        setExecutionProgress({
          status: execution.status === 'running' ? 'running' : 
                  execution.status === 'completed' ? 'success' : 
                  execution.status === 'failed' ? 'failed' : 'idle',
          progress: totalNodes > 0 ? (completedUniqueNodes.size / totalNodes) * 100 : 0,
          currentNode: runningCount > 0 ? nodeExecutions.find((ne: any) => ne.status === 'running')?.nodeId : undefined,
          totalNodes,
          completedNodes: nodeExecutions.length,
          completedUniqueNodes,
          loopIterations: {},
          expectedTotalExecutions: nodeExecutions.length,
          hasLoops: false,
          isFullWorkflowExecution: false, // Don't show confetti for recovered executions (we don't know the mode)
        });
        
        // Open execution panel if execution is running
        if (execution.status === 'running') {
          setIsExecutionPanelOpen(true);
        }
        
        // Don't show toast - state recovery should be silent
        // The execution panel will update automatically with recovered data
      }
    };
    
    const handleRecoveryFailed = (event: CustomEvent) => {
      toast.error('Failed to recover execution state', {
        description: event.detail.error || 'Unknown error',
      });
    };
    
    const handleExecutionNotFound = () => {
      // Don't clear execution state - test runs aren't persisted to DB,
      // so ExecutionNotFound will fire on SignalR reconnect but the
      // execution data is still valid in memory and should remain visible
    };
    
    // Listen to custom window events dispatched by SocketContext
    window.addEventListener('execution-state-recovered', handleStateRecovered as EventListener);
    window.addEventListener('execution-recovery-failed', handleRecoveryFailed as EventListener);
    window.addEventListener('execution-not-found', handleExecutionNotFound as EventListener);
    
    return () => {
      window.removeEventListener('execution-state-recovered', handleStateRecovered as EventListener);
      window.removeEventListener('execution-recovery-failed', handleRecoveryFailed as EventListener);
      window.removeEventListener('execution-not-found', handleExecutionNotFound as EventListener);
    };
  }, [nodes]); // Depend on nodes to calculate progress correctly

  // Function to update nodes with execution outputs from the store
  // Defined here after useWorkflowStore hook so setNodes is available
  const updateNodesWithExecutionOutputs = useCallback((store: Record<string, any>) => {
    // console.log('[WorkflowEditor] Updating nodes with execution outputs:', {
    //   storeKeys: Object.keys(store),
    //   nodeCount: nodes.length
    // });
    
    setNodes((currentNodes: Node[]) => {
      return currentNodes.map((node: Node) => {
        // Try to find output by node label first, then by node ID
        const nodeLabel = node.data?.label;
        let nodeOutput = nodeLabel ? store[nodeLabel] : undefined;
        
        if (!nodeOutput) {
          nodeOutput = store[node.id];
        }
        
        if (nodeOutput) {
          return {
            ...node,
            data: {
              ...node.data,
              executionOutput: nodeOutput,
              hasExecutionOutput: true,
              lastUpdated: Date.now(), // Track when this was cached
            },
          };
        }
        
        return node;
      });
    });
  }, [setNodes, nodes]);

  // Note: Execution store loading is now handled by BottomExecutionPanel

  // Memoize onConnect to ensure stable reference with validation
  // Don't include edges in dependencies - it causes the callback to be recreated
  const onConnect = useCallback((connection: any) => {
    
    const sourceNode = nodes.find(n => n.id === connection.source);
    const targetNode = nodes.find(n => n.id === connection.target);
    
    if (!sourceNode || !targetNode) {
      return;
    }
    
    // Get node definitions
    const sourceDef = ALL_NODE_DEFINITIONS[sourceNode.type];
    const targetDef = ALL_NODE_DEFINITIONS[targetNode.type];
    
    if (!sourceDef || !targetDef) return;
    
    // VALIDATION 1: Connections FROM anchors (AI Agent/Chat Memory Manager) TO nodes
    if (connection.sourceHandle && ['chatModel', 'memory', 'tools', 'parser'].includes(connection.sourceHandle)) {
      // Validate sub-node type matches anchor type
      if (connection.sourceHandle === 'chatModel') {
        // Only allow chat model sub-nodes
        if (targetDef.subNodeConfig?.category !== 'chatModel') {
          toast.error('Invalid Connection', {
            description: 'Only chat model sub-nodes can connect to the Model anchor. Try using Claude Chat Model or OpenAI Chat Model.',
          });
          return;
        }
        
        // Check if Model anchor already has a connection
        const existingConnection = edges.find(e => 
          e.source === connection.source && 
          e.sourceHandle === 'chatModel'
        );
        if (existingConnection) {
          toast.error('Connection Limit Reached', {
            description: 'The Model anchor can only have one connection. Remove the existing connection first.',
          });
          return;
        }
      } else if (connection.sourceHandle === 'memory') {
        // Only allow memory sub-nodes
        if (targetDef.subNodeConfig?.category !== 'memory') {
          toast.error('Invalid Connection', {
            description: 'Only memory sub-nodes can connect to the Memory anchor. Try using Simple Memory or LangChain Chat Memory.',
          });
          return;
        }
        
        // Check if Memory anchor already has a connection
        const existingConnection = edges.find(e => 
          e.source === connection.source && 
          e.sourceHandle === 'memory'
        );
        if (existingConnection) {
          toast.error('Connection Limit Reached', {
            description: 'The Memory anchor can only have one connection. Remove the existing connection first.',
          });
          return;
        }
      } else if (connection.sourceHandle === 'tools') {
        // Only allow tool-capable nodes
        if (!targetDef.toolCapable?.enabled) {
          toast.error('Invalid Connection', {
            description: 'Only tool-capable nodes can connect to the Tools anchor. Try using HTTP Request, Code Execution, or other tool nodes.',
          });
          return;
        }
        // Tools can have multiple connections, so no limit check
      } else if (connection.sourceHandle === 'parser') {
        // Only allow parser sub-nodes
        if (targetDef.subNodeConfig?.category !== 'parser') {
          toast.error('Invalid Connection', {
            description: 'Only parser sub-nodes can connect to the Parser anchor. Try using Agent Output Parser.',
          });
          return;
        }
        
        // Check if Parser anchor already has a connection
        const existingConnection = edges.find(e => 
          e.source === connection.source && 
          e.sourceHandle === 'parser'
        );
        if (existingConnection) {
          toast.error('Connection Limit Reached', {
            description: 'The Parser anchor can only have one connection. Remove the existing connection first.',
          });
          return;
        }
      }
      
      // Fix targetHandle for sub-node and tool connections
      // Sub-nodes expect 'subNodeInput', tool nodes expect 'default'
      if (connection.sourceHandle === 'chatModel' || connection.sourceHandle === 'memory' || connection.sourceHandle === 'parser' || connection.sourceHandle === 'pollSource') {
        connection.targetHandle = 'subNodeInput';
      } else if (connection.sourceHandle === 'tools') {
        connection.targetHandle = 'default';
      }
    } 
    // VALIDATION 2: Prevent regular nodes from connecting TO sub-nodes
    else if (targetDef.isSubNode) {
      // Sub-nodes can ONLY receive connections from anchors (chatModel, memory, tools, parser)
      // Regular node outputs should not connect to sub-nodes
      const subNodeCategory = targetDef.subNodeConfig?.category;
      const anchorName = subNodeCategory === 'chatModel' ? 'Model' : subNodeCategory === 'memory' ? 'Memory' : subNodeCategory === 'parser' ? 'Parser' : 'Tools';
      toast.error('Invalid Connection', {
        description: `${targetDef.name} is a sub-node and can only receive connections from the ${anchorName} anchor on AI Agent or Chat Memory Manager nodes.`,
      });
      return;
    }
    // VALIDATION 3: Prevent sub-nodes from connecting FROM their output TO regular nodes
    else if (sourceDef.isSubNode) {
      // Sub-nodes should not have regular outputs - they only connect via anchors
      const subNodeCategory = sourceDef.subNodeConfig?.category;
      const anchorName = subNodeCategory === 'chatModel' ? 'Model' : subNodeCategory === 'memory' ? 'Memory' : subNodeCategory === 'parser' ? 'Parser' : 'Tools';
      
      // Special case: if target is also a sub-node
      if (targetDef.isSubNode) {
        toast.error('Invalid Connection', {
          description: 'Sub-nodes cannot connect to other sub-nodes. Both must be connected to anchors on AI Agent or Chat Memory Manager nodes.',
        });
      } else {
        toast.error('Invalid Connection', {
          description: `${sourceDef.name} is a sub-node and must be connected to the ${anchorName} anchor on AI Agent or Chat Memory Manager nodes, not to regular nodes.`,
        });
      }
      return;
    }
    
    storeOnConnect(connection);
    
    // If this is a tool or pollSource connection, force React Flow to recalculate handle positions
    if ((connection.sourceHandle === 'tools' || connection.sourceHandle === 'pollSource') && connection.target) {
      // Schedule multiple updates to ensure handle positions are recalculated
      // First update happens quickly, second ensures it catches any delayed re-renders
      setTimeout(() => {
        updateNodeInternals(connection.target!);
      }, 50);
      setTimeout(() => {
        updateNodeInternals(connection.target!);
      }, 150);
    }
  }, [storeOnConnect, nodes, edges, updateNodeInternals]);

  // Validate connections during drag - provides real-time feedback
  const isValidConnection = useCallback((connection: any) => {
    const sourceNode = nodes.find(n => n.id === connection.source);
    const targetNode = nodes.find(n => n.id === connection.target);

    if (!sourceNode || !targetNode) return false;

    const sourceDef = ALL_NODE_DEFINITIONS[sourceNode.type];
    const targetDef = ALL_NODE_DEFINITIONS[targetNode.type];

    if (!sourceDef || !targetDef) return false;

    // Validate anchor connections
    if (connection.sourceHandle && ['chatModel', 'memory', 'tools', 'parser', 'pollSource'].includes(connection.sourceHandle)) {
      if (connection.sourceHandle === 'chatModel') {
        // Only allow chat model sub-nodes
        if (targetDef.subNodeConfig?.category !== 'chatModel') return false;
        // Check if already connected
        const existingConnection = edges.find(e =>
          e.source === connection.source && e.sourceHandle === 'chatModel'
        );
        if (existingConnection) return false;
      } else if (connection.sourceHandle === 'memory') {
        // Only allow memory sub-nodes
        if (targetDef.subNodeConfig?.category !== 'memory') return false;
        // Check if already connected
        const existingConnection = edges.find(e =>
          e.source === connection.source && e.sourceHandle === 'memory'
        );
        if (existingConnection) return false;
      } else if (connection.sourceHandle === 'tools') {
        // Only allow tool-capable nodes
        if (!targetDef.toolCapable?.enabled) return false;
      } else if (connection.sourceHandle === 'parser') {
        // Only allow parser sub-nodes
        if (targetDef.subNodeConfig?.category !== 'parser') return false;
        // Check if already connected
        const existingConnection = edges.find(e =>
          e.source === connection.source && e.sourceHandle === 'parser'
        );
        if (existingConnection) return false;
      } else if (connection.sourceHandle === 'pollSource') {
        // Only allow pollSource sub-nodes
        if (targetDef.subNodeConfig?.category !== 'pollSource') return false;
        // Check if already connected
        const existingConnection = edges.find(e =>
          e.source === connection.source && e.sourceHandle === 'pollSource'
        );
        if (existingConnection) return false;
      }
    }
    // Prevent regular nodes from connecting TO sub-nodes (except via anchors)
    else if (targetDef.isSubNode) {
      return false;
    }
    // Prevent sub-nodes from connecting FROM their output
    else if (sourceDef.isSubNode) {
      return false;
    }

    return true;
  }, [nodes, edges]);

  // Custom fitView handler with proper options
  const handleFitView = useCallback(() => {
    reactFlowFitView({
      padding: 0.2,
      includeHiddenNodes: false,
      minZoom: 0.1,
      maxZoom: 1.5,
      duration: 400,
      nodes: nodes.filter(n => !n.hidden), // Explicitly pass visible nodes
    });
  }, [reactFlowFitView, nodes]);

  // Fit view when execution panel opens/closes to keep workflow visible
  useEffect(() => {
    // Multiple delays to ensure fitView works after panel resize animation completes
    // The panel resize can take varying amounts of time depending on animation
    const timer1 = setTimeout(() => {
      reactFlowFitView({
        padding: 0.2,
        includeHiddenNodes: false,
        minZoom: 0.1,
        maxZoom: 1.5,
        duration: 200,
      });
    }, 50);

    // Second fitView after animation likely complete
    const timer2 = setTimeout(() => {
      reactFlowFitView({
        padding: 0.2,
        includeHiddenNodes: false,
        minZoom: 0.1,
        maxZoom: 1.5,
        duration: 300,
      });
    }, 350);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExecutionPanelOpen]); // Only trigger on panel open/close, not on node changes

  // Close node palette when execution panel opens
  useEffect(() => {
    if (isExecutionPanelOpen) {
      setPaletteForceCloseCounter(prev => prev + 1);
    }
  }, [isExecutionPanelOpen]);

  // Keyboard shortcuts (disabled when node editor modal is open)
  useKeyboardShortcuts([
    {
      key: 's',
      ctrl: true,
      description: 'Save workflow',
      action: () => handleSave(),
    },
    {
      key: 'Enter',
      ctrl: true,
      description: 'Execute workflow',
      action: () => handleExecute(),
    },
    {
      key: '`',
      ctrl: true,
      description: 'Toggle execution panel',
      action: () => setIsExecutionPanelOpen(!isExecutionPanelOpen),
    },
    {
      key: 'c',
      ctrl: true,
      description: 'Copy selected nodes',
      action: () => {
        const selectedNodes = nodes.filter((n) => n.selected);
        if (selectedNodes.length > 0) {
          copyNodes(selectedNodes);
          toast.success(`Copied ${selectedNodes.length} node(s)`);
        }
      },
    },
    {
      key: 'v',
      ctrl: true,
      description: 'Paste nodes',
      action: () => {
        pasteNodes();
      },
    },
    {
      key: 'x',
      ctrl: true,
      description: 'Cut selected nodes',
      action: () => {
        const selectedNodes = nodes.filter((n) => n.selected);
        if (selectedNodes.length > 0) {
          cutNodes(selectedNodes);
          toast.success(`Cut ${selectedNodes.length} node(s)`);
        }
      },
    },
    {
      key: 'd',
      ctrl: true,
      description: 'Duplicate selected nodes',
      action: () => {
        const selectedNodes = nodes.filter((n) => n.selected);
        if (selectedNodes.length > 0) {
          duplicateNodes(selectedNodes);
        }
      },
    },
    {
      key: 'a',
      ctrl: true,
      description: 'Select all nodes',
      action: () => {
        selectAllNodes();
        toast.success('Selected all nodes');
      },
    },
    {
      key: 'Delete',
      description: 'Delete selected nodes',
      action: () => {
        const selectedNodes = nodes.filter((n) => n.selected);
        if (selectedNodes.length > 0) {
          deleteSelectedNodes();
          toast.success(`Deleted ${selectedNodes.length} node(s)`);
        } else if (selectedNode) {
          deleteNode(selectedNode.id);
          setSelectedNode(null);
          toast.success('Node deleted');
        }
      },
    },
    {
      key: 'Backspace',
      description: 'Delete selected nodes',
      action: () => {
        const selectedNodes = nodes.filter((n) => n.selected);
        if (selectedNodes.length > 0) {
          deleteSelectedNodes();
          toast.success(`Deleted ${selectedNodes.length} node(s)`);
        } else if (selectedNode) {
          deleteNode(selectedNode.id);
          setSelectedNode(null);
          toast.success('Node deleted');
        }
      },
    },
    {
      key: 'Escape',
      description: 'Deselect all nodes',
      action: () => {
        deselectAllNodes();
        setSelectedNode(null);
      },
    },
    {
      key: '0',
      ctrl: true,
      description: 'Fit view to visible nodes',
      action: () => {
        handleFitView();
      },
    },
    {
      key: '?',
      description: 'Show keyboard shortcuts',
      action: () => setShowShortcutsDialog(true),
      preventDefault: false,
    },
    ...(isWorkflowAssistantEnabled ? [{
      key: 'a',
      ctrl: true,
      shift: true,
      description: 'Open AI Assistant',
      action: () => setIsAssistantOpen(prev => !prev),
    }] : []),
  ], !isNodeEditorOpen);

  // Persist workflow state to sessionStorage on page refresh
  // Continuously save workflow state to sessionStorage (for refresh persistence)
  useEffect(() => {
    if (!id || nodes.length === 0) return;

    // Debounce the save to avoid too many writes
    const timeoutId = setTimeout(() => {
      try {
        // Strip out execution data to prevent quota exceeded errors
        // executionOutput can contain large HTTP responses, etc.
        const cleanNodes = nodes.map(node => {
          const { executionOutput, cachedInputData, ...cleanData } = node.data || {};
          return { ...node, data: cleanData };
        });

        sessionStorage.setItem(`workflow-${id}-nodes`, JSON.stringify(cleanNodes));
        sessionStorage.setItem(`workflow-${id}-edges`, JSON.stringify(edges));
        sessionStorage.setItem('isRefreshing', 'true');
        // console.log('💾 Auto-saved to sessionStorage:', {
        //   nodeCount: nodes.length,
        //   edgeCount: edges.length,
        // });

        // Save viewport position to maintain zoom/pan state
        const viewport = getViewport();
        sessionStorage.setItem(`workflow-${id}-viewport`, JSON.stringify(viewport));
      } catch (error) {
        // Silently fail - sessionStorage save is not critical
        // This can happen if quota is exceeded even after stripping execution data
        console.warn('Failed to save workflow to sessionStorage:', error);
      }
    }, 500); // Save 500ms after last change

    return () => clearTimeout(timeoutId);
  }, [id, nodes, edges, getViewport]);

  // Restore workflow state from sessionStorage BEFORE loading (prevents flash)
  useEffect(() => {
    if (!id) return;

    const isRefresh = sessionStorage.getItem('isRefreshing');
    
    if (isRefresh === 'true') {
      const savedNodes = sessionStorage.getItem(`workflow-${id}-nodes`);
      const savedEdges = sessionStorage.getItem(`workflow-${id}-edges`);
      const savedViewport = sessionStorage.getItem(`workflow-${id}-viewport`);
      
      // console.log('📦 SessionStorage data:', { 
      //   hasNodes: !!savedNodes, 
      //   hasEdges: !!savedEdges,
      //   nodeCount: savedNodes ? JSON.parse(savedNodes).length : 0 
      // });
      
      if (savedNodes && savedEdges) {
        try {
          // Restore the visual state immediately to prevent empty canvas flash
          const parsedNodes = JSON.parse(savedNodes);
          const parsedEdges = JSON.parse(savedEdges);
          // Validate edges before restoring
          const validatedEdges = validateEdges(parsedEdges, parsedNodes);
          setNodes(parsedNodes);
          setEdges(validatedEdges);
          
          // Restore viewport position (zoom/pan state)
          if (savedViewport) {
            try {
              const viewport = JSON.parse(savedViewport);
              setViewport(viewport, { duration: 0 });
            } catch (error) {
              // Silently fail - viewport restore is not critical
            }
          }
          
          setLoading(false);
        } catch (error) {
          // Silently fail - state restore is not critical
        }
      }
      
      // Clear the refresh flag
      sessionStorage.removeItem('isRefreshing');
    }
  }, [id, setNodes, setEdges]);

  // Load workflow from API and subscribe to workflow updates
  useEffect(() => {
    if (id) {
      loadWorkflow();
      
      // Subscribe to workflow updates via SignalR
      if (isConnected && socket) {
        socket.invoke('SubscribeToWorkflow', id)
          .then(() => {})
          .catch(err => console.error('[WorkflowEditor] ❌ Error subscribing to workflow:', err));
      }
    }

    // Cleanup on unmount - clear all workflow state to free memory
    return () => {
      setCurrentExecution(null);
      clearWorkflow();

      // Unsubscribe from workflow updates
      if (id && isConnected && socket) {
        socket.invoke('UnsubscribeFromWorkflow', id)
          .then(() => {})
          .catch(err => console.error('[WorkflowEditor] ❌ Error unsubscribing from workflow:', err));
      }
      
      // Clean up sessionStorage on unmount (not refresh)
      sessionStorage.removeItem(`workflow-${id}-nodes`);
      sessionStorage.removeItem(`workflow-${id}-edges`);
      sessionStorage.removeItem(`workflow-${id}-viewport`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isConnected, socket]);

  // Get ReactFlow instance for drag and drop and viewport control
  const reactFlowInstance = useReactFlow();

  const loadWorkflow = async () => {
    try {
      // Check if we're restoring from a refresh (to skip fitView animation)
      const isRestoringFromRefresh = sessionStorage.getItem(`workflow-${id}-nodes`) !== null;
      
      const response = await workflowsApi.get(id!);
      setWorkflow(response.data);

      // Store workflow metadata for context-aware features (e.g., KB collection filtering)
      setCurrentWorkflowMetadata({
        id: response.data.id,
        name: response.data.name,
        workflowLocationType: response.data.workflowLocationType,
        teamFolderId: response.data.teamFolderId,
        folderId: response.data.folderId,
      });

      // Clear any selected node when loading a workflow
      setSelectedNode(null);
      
      // Check if workflow needs migration
      const workflowNeedsMigration = needsMigration(response.data.definition);
      if (workflowNeedsMigration) {
      }
      
      // Enrich nodes with category information from nodeDefinitions
      // Also strip out any cached executionOutput to prevent memory bloat
      let loadedNodes = (response.data.definition?.nodes || []).map((node: Node) => {
        const nodeDefinition = nodeDefinitions[node.type || ''];
        const { executionOutput, ...cleanData } = node.data || {};
        return {
          ...node,
          data: {
            ...cleanData,
            category: cleanData?.category || nodeDefinition?.category || 'action',
            nodeType: cleanData?.nodeType || node.type,
          },
        };
      });
      
      // Migrate nodes if needed
      if (workflowNeedsMigration) {
        loadedNodes = migrateAllNodes(loadedNodes);
      }
      
      
      setNodes(loadedNodes);
      // Ensure all edges have the correct type set and validate them
      const loadedEdges = (response.data.definition?.edges || []).map((edge: any) => ({
        ...edge,
        type: edge.type || 'custom',
      }));
      // Validate edges to ensure they reference existing nodes
      const validatedEdges = validateEdges(loadedEdges, loadedNodes);
      setEdges(validatedEdges);

      // Store saved state for unsaved changes detection
      const cleanNodesForSave = loadedNodes.map((node: Node) => {
        const { executionOutput, cachedInputData, ...cleanData } = node.data || {};
        return { ...node, data: cleanData };
      });
      savedStateRef.current = {
        nodes: JSON.stringify(cleanNodesForSave),
        edges: JSON.stringify(validatedEdges),
      };
      setSavedVersion(v => v + 1); // Trigger re-computation of hasUnsavedChanges

      // Only fit view if NOT restoring from refresh (prevents jarring viewport change)
      if (!isRestoringFromRefresh) {
        // Fit the nodes in the viewport after a short delay to ensure nodes are rendered
        // This provides a better default view that shows all visible content
        setTimeout(() => {
          if (loadedNodes.length > 0) {
            reactFlowInstance.fitView({ 
              padding: 0.2, // 20% padding around the nodes
              duration: 400, // Smooth animation
              maxZoom: 1.5, // Don't zoom in too much
              minZoom: 0.1, // Allow zooming out
              includeHiddenNodes: false,
            });
          } else {
            // If no nodes, reset to a centered view
            reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 1 }, { duration: 400 });
          }
        }, 100);
      }
    } catch (error) {
      toast.error('Failed to load workflow');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!workflow) return;

    // Show saving toast
    const savingToast = toast.loading('Saving workflow...');

    setSaving(true);
    try {
      // Enrich nodes with category information from nodeDefinitions
      // Also strip out transient test data (executionOutput, cachedInputData) before saving
      const enrichedNodes = nodes.map(node => {
        const { executionOutput, cachedInputData, ...cleanData } = node.data || {};
        return {
          ...node,
          data: cleanData,
        };
      }).map(node => {
        const nodeDefinition = node.type ? nodeDefinitions[node.type] : undefined;
        return {
          ...node,
          data: {
            ...node.data,
            category: node.data?.category || nodeDefinition?.category || 'action',
            nodeType: node.type,
          }
        };
      });

      await workflowsApi.update(workflow.id as string, {
        name: workflow.name,
        description: workflow.description,
        definition: {
          nodes: enrichedNodes,
          edges,
          settings: (workflow.definition as any)?.settings || {},
        },
        active: workflow.active,
        folderId: workflow.folderId,
        tags: workflow.tags,
      });
      
      // Version is now automatically created by the backend when workflow data changes
      // Reset current version number (we're now on the latest)
      setCurrentVersionNumber(null);
      // Trigger version indicator refresh
      setVersionRefreshTrigger(prev => prev + 1);

      // Update saved state for unsaved changes detection
      const cleanNodesAfterSave = nodes.map(node => {
        const { executionOutput, cachedInputData, ...cleanData } = node.data || {};
        return { ...node, data: cleanData };
      });
      savedStateRef.current = {
        nodes: JSON.stringify(cleanNodesAfterSave),
        edges: JSON.stringify(edges),
      };
      setSavedVersion(v => v + 1); // Trigger re-computation of hasUnsavedChanges

      toast.success('Workflow saved successfully', { id: savingToast });
    } catch (error: any) {
      
      // Show detailed error message
      const errorData = error.response?.data;
      let errorMessage = error.response?.data?.error || error.message || 'Failed to save workflow';
      
      // If there are validation details, show them
      if (errorData?.details && Array.isArray(errorData.details)) {
        errorMessage = `${errorMessage}:\n${errorData.details.join('\n')}`;
      }
      
      toast.error(errorMessage, { 
        id: savingToast,
        duration: 8000, // Show longer for detailed errors
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLoadVersion = (version: any) => {
    
    // Parse the version data
    const nodes = JSON.parse(version.nodes);
    const edges = JSON.parse(version.edges);
    const variables = version.variables ? JSON.parse(version.variables) : undefined;
    
    // Update the workflow state with the version data
    setWorkflow({
      ...workflow,
      name: version.name,
      description: version.description,
      definition: {
        nodes,
        edges,
        settings: (workflow as any)?.definition?.settings || {},
        staticData: variables,
      },
    });
    
    // Update nodes and edges in ReactFlow
    setNodes(nodes);
    setEdges(edges);
    
    // Track which version is currently loaded
    setCurrentVersionNumber(version.versionNumber);
    
    toast.info(`Viewing version ${version.versionNumber}. Click Save to create a new version.`);
  };

  const handleExecute = async () => {
    if (!workflow) {
      toast.error('No workflow loaded');
      return;
    }

    // Check if workflow has an ID
    if (!workflow.id) {
      console.error('[WorkflowEditor] Workflow missing ID:', workflow);
      toast.error('Workflow ID is missing. Please save the workflow first.');
      return;
    }

    // Validate workflow before execution
    const validationResult = validationApi.validateWorkflow(workflow);
    if (!validationResult.valid) {
      setValidationErrors(validationResult.errors);
      setValidationWarnings(validationResult.warnings);
      toast.error('Workflow validation failed. Please fix the errors before executing.');
      return;
    }

    // Clear any previous validation errors
    setValidationErrors([]);
    setValidationWarnings([]);

    // Auto-save workflow before execution to ensure latest changes are executed
    const savingToast = toast.loading('Saving workflow before execution...');
    
    try {
      // Enrich nodes with category information from nodeDefinitions
      // Also strip out transient test data (executionOutput, cachedInputData) before saving
      const enrichedNodes = nodes.map(node => {
        const { executionOutput, cachedInputData, ...cleanData } = node.data || {};
        return {
          ...node,
          data: cleanData,
        };
      }).map(node => {
        const nodeDefinition = node.type ? nodeDefinitions[node.type] : undefined;
        return {
          ...node,
          data: {
            ...node.data,
            category: node.data?.category || nodeDefinition?.category || 'action',
            nodeType: node.type,
          }
        };
      });

      await workflowsApi.update(workflow.id as string, {
        name: workflow.name,
        description: workflow.description,
        definition: {
          nodes: enrichedNodes,
          edges,
          settings: (workflow.definition as any)?.settings || {},
        },
        active: workflow.active,
        folderId: workflow.folderId,
        tags: workflow.tags,
      });
      
      toast.success('Workflow saved', { id: savingToast });
    } catch (error: any) {
      const errorData = error.response?.data;
      let errorMessage = error.response?.data?.error || error.message || 'Failed to save workflow';
      
      if (errorData?.details && Array.isArray(errorData.details)) {
        errorMessage = `${errorMessage}:\n${errorData.details.join('\n')}`;
      }
      
      toast.error(errorMessage, { 
        id: savingToast,
        duration: 8000,
      });
      return; // Don't execute if save failed
    }


    // Show toast immediately
    toast.info('Starting workflow execution...');
    
    // Open execution panel immediately
    setIsExecutionPanelOpen(true);

    try {
      // Note: The backend executes synchronously - this call returns AFTER execution completes
      const response = await workflowsApi.execute(workflow.id as string);

      // Set execution with the ID from the response
      const executionId = response.data.executionId;

      if (executionId) {
        // Since the API returns only after execution completes, set status to 'success' immediately
        // This avoids a race condition where SignalR broadcasts completion before we subscribe
        setCurrentExecution({
          id: executionId,
          workflow_id: workflow.id as string,
          status: 'success',
          trigger_type: 'manual',
          created_at: Date.now(),
          completed_at: Date.now(),
        });

        // Subscribe to execution updates via SignalR (for any late events or cleanup)
        subscribeToExecution(executionId);

        // Set execution progress to completed since execution already finished
        setExecutionProgress({
          status: 'success',
          progress: 100,
          currentNode: undefined,
          totalNodes: nodes.length,
          completedNodes: nodes.length,
          completedUniqueNodes: new Set<string>(),
          loopIterations: {},
          expectedTotalExecutions: nodes.length,
          hasLoops: false,
          isFullWorkflowExecution: true,
        });

        // Clear node/edge execution statuses after a short delay for visual feedback
        setTimeout(() => {
          setNodeExecutionStatuses({});
          setEdgeExecutionStatuses({});
        }, 2000);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to execute workflow';
      toast.error(errorMessage);
      setIsExecutionPanelOpen(false);
      setCurrentExecution(null);
      // Clear node/edge execution statuses to stop spinning animations
      setNodeExecutionStatuses({});
      setEdgeExecutionStatuses({});
    }
  };

  const handleSaveVariables = async (variables: Record<string, any>) => {
    if (!workflow) return;

    const definition = workflow.definition as any;
    const updatedDefinition = {
      ...definition,
      staticData: variables,
    };

    await workflowsApi.update(workflow.id as string, {
      definition: updatedDefinition,
    });

    // Update local state
    setWorkflow({
      ...workflow,
      definition: updatedDefinition,
    });
  };

  const handleToggleActive = async (checked: boolean) => {
    if (!workflow) return;

    try {
      // Use the toggle endpoint like WorkflowList does
      const response = await workflowsApi.toggleActive(workflow.id as string);

      // Update local state with the response data
      setWorkflow({
        ...workflow,
        active: response.data.active,
      });

      toast.success(response.data.active ? 'Workflow activated' : 'Workflow deactivated');
    } catch (error) {
      toast.error('Failed to update workflow status');
    }
  };

  const handleNameChange = async (newName: string) => {
    if (!workflow) return;

    try {
      await workflowsApi.update(workflow.id as string, {
        name: newName,
      });

      // Update local state
      setWorkflow({
        ...workflow,
        name: newName,
      });

      toast.success('Workflow name updated');
    } catch (error) {
      toast.error('Failed to update workflow name');
    }
  };

  const handleDescriptionChange = async (newDescription: string) => {
    if (!workflow) return;

    try {
      await workflowsApi.update(workflow.id as string, {
        description: newDescription,
      });

      // Update local state
      setWorkflow({
        ...workflow,
        description: newDescription,
      });

      toast.success('Workflow description updated');
    } catch (error) {
      toast.error('Failed to update workflow description');
    }
  };

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
  }, [setSelectedNode]);

  const onNodeDoubleClick = useCallback((_event: React.MouseEvent, node: Node) => {
    // For labels, don't open the editor - let the component handle editing
    if (node.type === 'label') {
      return;
    }
    
    // Check if node is disabled
    const isDisabled = node.data?.disabled || node.data?.settings?.disabled || false;
    if (isDisabled) {
      toast.info('Cannot edit disabled node. Enable the node first to edit its properties.');
      return;
    }
    
    setEditingNodeId(node.id);
    setIsNodeEditorOpen(true);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    selectEdge(null);
  }, [setSelectedNode, selectEdge]);

  // Pane context menu handler (right-click on empty canvas)
  const onPaneContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    setCanvasMenuPosition({ x: event.clientX, y: event.clientY });
    setCanvasMenuOpen(true);
    setEdgeMenuOpen(false); // Close edge menu if open
  }, []);



  // Edge click handler
  const onEdgeClick = useCallback((_event: React.MouseEvent, edge: Edge) => {
    selectEdge(edge);
    setSelectedNode(null);
  }, [selectEdge, setSelectedNode]);

  // Node context menu handler
  const onNodeContextMenu = useCallback((event: React.MouseEvent, node: Node) => {
    event.preventDefault();
    event.stopPropagation();
    // Close other context menus
    setCanvasMenuOpen(false);
    setEdgeMenuOpen(false);
    // The NodeContextMenu component will handle the menu display
  }, []);

  // Edge context menu handler
  const onEdgeContextMenu = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuEdgeId(edge.id);
    setEdgeMenuPosition({ x: event.clientX, y: event.clientY });
    setEdgeMenuOpen(true);
    setCanvasMenuOpen(false); // Close canvas menu if open
  }, []);

  // Prevent browser context menu globally
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      // Check if the event target is within a Radix UI context menu content
      const target = e.target as HTMLElement;
      const isInContextMenuContent = target.closest('[data-radix-context-menu-content]') || 
                                      target.closest('[role="menu"]');
      
      // If not in a context menu content, prevent the browser menu
      if (!isInContextMenuContent) {
        e.preventDefault();
      }
    };

    // Add listener in bubble phase (not capture) so Radix can handle it first
    document.addEventListener('contextmenu', handleContextMenu, false);
    return () => document.removeEventListener('contextmenu', handleContextMenu, false);
  }, []);

  // Drag and drop handlers
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';

    // Skip drop-on-node feature if not dragging from palette
    const draggingType = draggingNodeTypeRef.current;
    if (!draggingType) {
      setHighlightedNodeId(null);
      return;
    }

    // Skip if dragging a sub-node or trigger (they shouldn't auto-connect)
    const draggingDef = ALL_NODE_DEFINITIONS[draggingType];
    const draggingDisplayDef = nodeDefinitions[draggingType];
    if (draggingDef?.isSubNode || draggingDisplayDef?.category === 'triggers') {
      setHighlightedNodeId(null);
      return;
    }

    // Get the flow position of the cursor
    const position = reactFlowInstance.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    // Find if cursor is near any existing node (expanded hit area for easier targeting)
    const hitPadding = 40; // Expand detection area by 40px on each side
    const hoveredNode = nodes.find(node => {
      const nodeWidth = node.width || 96;
      const nodeHeight = node.height || 96;
      return (
        position.x >= node.position.x - hitPadding &&
        position.x <= node.position.x + nodeWidth + hitPadding &&
        position.y >= node.position.y - hitPadding &&
        position.y <= node.position.y + nodeHeight + hitPadding
      );
    });

    if (hoveredNode) {
      // Check if target node is valid drop target (not a sub-node)
      const hoveredDef = ALL_NODE_DEFINITIONS[hoveredNode.type || ''];
      const isSubNode = hoveredDef?.isSubNode;

      // Valid if: not a sub-node
      if (!isSubNode) {
        setHighlightedNodeId(hoveredNode.id);
        setHighlightedEdgeId(null);
      } else {
        setHighlightedNodeId(null);
        setHighlightedEdgeId(null);
      }
    } else {
      // Check if cursor is near any edge midpoint
      const edgeHitRadius = 30; // Distance from edge midpoint to trigger highlight
      let hoveredEdge: Edge | null = null;

      for (const edge of edges) {
        const sourceNode = nodes.find(n => n.id === edge.source);
        const targetNode = nodes.find(n => n.id === edge.target);

        if (!sourceNode || !targetNode) continue;

        // Skip edges from/to sub-nodes (anchor connections)
        const sourceDef = ALL_NODE_DEFINITIONS[sourceNode.type || ''];
        const targetDef = ALL_NODE_DEFINITIONS[targetNode.type || ''];
        if (sourceDef?.isSubNode || targetDef?.isSubNode) continue;

        // Calculate edge midpoint (simplified - uses node centers)
        const sourceWidth = sourceNode.width || 96;
        const sourceHeight = sourceNode.height || 96;
        const targetWidth = targetNode.width || 96;
        const targetHeight = targetNode.height || 96;

        const sourceCenter = {
          x: sourceNode.position.x + sourceWidth / 2,
          y: sourceNode.position.y + sourceHeight / 2,
        };
        const targetCenter = {
          x: targetNode.position.x + targetWidth / 2,
          y: targetNode.position.y + targetHeight / 2,
        };

        const midpoint = {
          x: (sourceCenter.x + targetCenter.x) / 2,
          y: (sourceCenter.y + targetCenter.y) / 2,
        };

        // Check distance from cursor to midpoint
        const distance = Math.sqrt(
          Math.pow(position.x - midpoint.x, 2) +
          Math.pow(position.y - midpoint.y, 2)
        );

        if (distance < edgeHitRadius) {
          hoveredEdge = edge;
          break;
        }
      }

      if (hoveredEdge) {
        setHighlightedNodeId(null);
        setHighlightedEdgeId(hoveredEdge.id);
      } else {
        setHighlightedNodeId(null);
        setHighlightedEdgeId(null);
      }
    }
  }, [reactFlowInstance, nodes, edges, setHighlightedNodeId, setHighlightedEdgeId]);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');

      if (!type) {
        return;
      }

      // Check if we're dropping onto a highlighted node or edge
      const targetNodeId = highlightedNodeId;
      const targetEdgeId = highlightedEdgeId;
      setHighlightedNodeId(null); // Clear highlights
      setHighlightedEdgeId(null);

      let position;
      let targetNode: Node | undefined;
      let targetEdge: Edge | undefined;

      if (targetNodeId) {
        // Find the target node and position new node to its right
        targetNode = nodes.find(n => n.id === targetNodeId);
        if (targetNode) {
          const targetWidth = targetNode.width || 96;
          const gap = 100; // 1 node width spacing
          position = {
            x: targetNode.position.x + targetWidth + gap,
            y: targetNode.position.y,
          };
        }
      } else if (targetEdgeId) {
        // Dropping on an edge - insert between connected nodes
        targetEdge = edges.find(e => e.id === targetEdgeId);
        // Position will be calculated in the edge insertion block below
      }

      if (!position) {
        // Default: drop at cursor position
        position = reactFlowInstance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });
      }

      // Get node definition to include category and other metadata
      const nodeDefinition = nodeDefinitions[type];
      const fullNodeDef = ALL_NODE_DEFINITIONS[type];

      // Determine node size based on category (complex nodes are larger)
      const isComplexNode = nodeDefinition?.category === 'triggers' ||
                           (nodeDefinition?.category as string) === 'flow-control';
      const nodeWidth = isComplexNode ? 128 : 96;
      const nodeHeight = isComplexNode ? 128 : 96;

      // Get default properties from node defaults
      const defaultProperties = getNodeDefaults(type);

      // Generate unique label to prevent duplicates
      const baseLabel = nodeDefinition?.label || type.charAt(0).toUpperCase() + type.slice(1);
      const existingLabels = getNodeLabels(nodes);
      const uniqueLabel = generateUniqueNodeLabel(baseLabel, existingLabels);

      const newNodeId = `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newNode: Node = {
        id: newNodeId,
        type,
        position,
        data: {
          label: uniqueLabel,
          category: nodeDefinition?.category || 'action',
          nodeType: type,
          parameters: defaultProperties,
        },
        width: nodeWidth,
        height: nodeHeight,
      };

      // Don't create edge if new node is a sub-node or trigger (no inputs)
      const isSubNode = fullNodeDef?.isSubNode;
      const isTrigger = nodeDefinition?.category === 'triggers';

      // Handle edge insertion (drop on edge)
      if (targetEdge && !isSubNode && !isTrigger) {
        const sourceNode = nodes.find(n => n.id === targetEdge.source);
        const edgeTargetNode = nodes.find(n => n.id === targetEdge.target);

        if (sourceNode && edgeTargetNode) {
          const sourceWidth = sourceNode.width || 96;
          const sourceHeight = sourceNode.height || 96;
          const gap = 100; // 1 node width spacing

          // Calculate new node position: 1 gap to the right of source node
          const insertPosition = {
            x: sourceNode.position.x + sourceWidth + gap,
            y: sourceNode.position.y + (sourceHeight - nodeHeight) / 2, // Vertically centered with source
          };

          // Calculate how much to shift downstream nodes
          const requiredSpaceEnd = insertPosition.x + nodeWidth + gap;
          const currentTargetX = edgeTargetNode.position.x;
          const shiftAmount = Math.max(0, requiredSpaceEnd - currentTargetX);

          // Build shifted nodes array (excluding source, shifting downstream nodes)
          const shiftedNodes = nodes.map(node => {
            if (node.id === sourceNode.id) return node;
            if (shiftAmount > 0 && node.position.x >= currentTargetX - 10) {
              return {
                ...node,
                position: { ...node.position, x: node.position.x + shiftAmount },
              };
            }
            return node;
          });

          // Create new node with correct position
          const insertedNode: Node = {
            ...newNode,
            position: insertPosition,
          };

          // Get handles from the original edge
          const originalSourceHandle = targetEdge.sourceHandle || 'default';
          const originalTargetHandle = targetEdge.targetHandle || 'default';

          // Get input/output handles for the new node
          const newNodeInputs = fullNodeDef?.inputs || [{ name: 'default' }];
          const newNodeOutputs = fullNodeDef?.outputs || [{ name: 'default' }];
          const newNodeInputHandle = newNodeInputs[0]?.name || 'default';
          const newNodeOutputHandle = newNodeOutputs[0]?.name || 'default';

          // Create two new edges: source→newNode and newNode→target
          const edge1: Edge = {
            id: `e${sourceNode.id}-${newNodeId}-${Date.now()}`,
            source: sourceNode.id,
            target: newNodeId,
            sourceHandle: originalSourceHandle,
            targetHandle: newNodeInputHandle,
            type: 'custom',
          };

          const edge2: Edge = {
            id: `e${newNodeId}-${edgeTargetNode.id}-${Date.now() + 1}`,
            source: newNodeId,
            target: edgeTargetNode.id,
            sourceHandle: newNodeOutputHandle,
            targetHandle: originalTargetHandle,
            type: 'custom',
          };

          // Single batch update: add new node to shifted nodes, update edges
          setNodes([...shiftedNodes, insertedNode]);
          setEdges([...edges.filter(e => e.id !== targetEdge.id), edge1, edge2]);

          toast.success(`Inserted ${uniqueLabel} node between ${sourceNode.data?.label || sourceNode.type} and ${edgeTargetNode.data?.label || edgeTargetNode.type}`);
          return; // Early return - we're done
        }
      }
      // Handle drop on node
      else if (targetNode && !isSubNode && !isTrigger) {
        setNodes([...nodes, newNode]);

        // Get the first available output from the target node
        const targetDef = ALL_NODE_DEFINITIONS[targetNode.type || ''];
        const outputs = targetDef?.outputs || [{ name: 'default' }];
        const sourceHandle = outputs[0]?.name || 'default';

        // Get the first available input for the new node
        const inputs = fullNodeDef?.inputs || [{ name: 'default' }];
        const targetHandle = inputs[0]?.name || 'default';

        const newEdge: Edge = {
          id: `e${targetNode.id}-${newNodeId}-${Date.now()}`,
          source: targetNode.id,
          target: newNodeId,
          sourceHandle,
          targetHandle,
          type: 'custom',
        };

        setEdges([...edges, newEdge]);
        toast.success(`Added ${uniqueLabel} node and connected to ${targetNode.data?.label || targetNode.type}`);
      }
      // Default: just add the node
      else {
        setNodes([...nodes, newNode]);
        toast.success(`Added ${uniqueLabel} node`);
      }
    },
    [reactFlowInstance, nodes, setNodes, highlightedNodeId, setHighlightedNodeId, highlightedEdgeId, setHighlightedEdgeId, edges, setEdges]
  );

  // Node drag handlers for existing canvas nodes (drop-on-node/edge feature)
  const onNodeDragStart = useCallback((_event: React.MouseEvent, node: Node) => {
    // Track the node being dragged
    draggingCanvasNodeRef.current = { id: node.id, type: node.type || 'default' };
  }, []);

  const onNodeDrag = useCallback((_event: React.MouseEvent, node: Node) => {
    if (!draggingCanvasNodeRef.current) return;

    const draggingType = draggingCanvasNodeRef.current.type;
    const draggingId = draggingCanvasNodeRef.current.id;

    // Skip if dragging a sub-node or trigger
    const draggingDef = ALL_NODE_DEFINITIONS[draggingType];
    const draggingDisplayDef = nodeDefinitions[draggingType];
    if (draggingDef?.isSubNode || draggingDisplayDef?.category === 'triggers') {
      setHighlightedNodeId(null);
      setHighlightedEdgeId(null);
      return;
    }

    const position = node.position;
    const nodeWidth = node.width || 96;
    const nodeHeight = node.height || 96;
    const nodeCenter = {
      x: position.x + nodeWidth / 2,
      y: position.y + nodeHeight / 2,
    };

    // Check for node hover (excluding self)
    const hitPadding = 40;
    const hoveredNode = nodes.find(n => {
      if (n.id === draggingId) return false; // Don't match self
      const nWidth = n.width || 96;
      const nHeight = n.height || 96;
      return (
        nodeCenter.x >= n.position.x - hitPadding &&
        nodeCenter.x <= n.position.x + nWidth + hitPadding &&
        nodeCenter.y >= n.position.y - hitPadding &&
        nodeCenter.y <= n.position.y + nHeight + hitPadding
      );
    });

    if (hoveredNode) {
      const hoveredDef = ALL_NODE_DEFINITIONS[hoveredNode.type || ''];
      if (!hoveredDef?.isSubNode) {
        setHighlightedNodeId(hoveredNode.id);
        setHighlightedEdgeId(null);
        return;
      }
    }

    // Check for edge hover (excluding edges connected to dragging node)
    const edgeHitRadius = 30;
    let hoveredEdge: Edge | null = null;

    for (const edge of edges) {
      // Skip edges connected to the dragging node
      if (edge.source === draggingId || edge.target === draggingId) continue;

      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);
      if (!sourceNode || !targetNode) continue;

      const sourceDef = ALL_NODE_DEFINITIONS[sourceNode.type || ''];
      const targetDef = ALL_NODE_DEFINITIONS[targetNode.type || ''];
      if (sourceDef?.isSubNode || targetDef?.isSubNode) continue;

      const sourceWidth = sourceNode.width || 96;
      const sourceHeight = sourceNode.height || 96;
      const targetWidth = targetNode.width || 96;
      const targetHeight = targetNode.height || 96;

      const sourceCenter = {
        x: sourceNode.position.x + sourceWidth / 2,
        y: sourceNode.position.y + sourceHeight / 2,
      };
      const targetCenter = {
        x: targetNode.position.x + targetWidth / 2,
        y: targetNode.position.y + targetHeight / 2,
      };

      const midpoint = {
        x: (sourceCenter.x + targetCenter.x) / 2,
        y: (sourceCenter.y + targetCenter.y) / 2,
      };

      const distance = Math.sqrt(
        Math.pow(nodeCenter.x - midpoint.x, 2) +
        Math.pow(nodeCenter.y - midpoint.y, 2)
      );

      if (distance < edgeHitRadius) {
        hoveredEdge = edge;
        break;
      }
    }

    if (hoveredEdge) {
      setHighlightedNodeId(null);
      setHighlightedEdgeId(hoveredEdge.id);
    } else {
      setHighlightedNodeId(null);
      setHighlightedEdgeId(null);
    }
  }, [nodes, edges, setHighlightedNodeId, setHighlightedEdgeId]);

  const onNodeDragStop = useCallback((_event: React.MouseEvent, node: Node) => {
    if (!draggingCanvasNodeRef.current) return;

    const draggingId = draggingCanvasNodeRef.current.id;
    const draggingType = draggingCanvasNodeRef.current.type;
    draggingCanvasNodeRef.current = null;

    const targetNodeId = highlightedNodeId;
    const targetEdgeId = highlightedEdgeId;
    setHighlightedNodeId(null);
    setHighlightedEdgeId(null);

    // Skip if dragging a sub-node or trigger
    const draggingDef = ALL_NODE_DEFINITIONS[draggingType];
    const draggingDisplayDef = nodeDefinitions[draggingType];
    if (draggingDef?.isSubNode || draggingDisplayDef?.category === 'triggers') {
      return;
    }

    // Handle drop on node - create edge from target to dragged node
    if (targetNodeId) {
      const targetNode = nodes.find(n => n.id === targetNodeId);
      if (targetNode) {
        // Check if edge already exists
        const edgeExists = edges.some(e =>
          e.source === targetNode.id && e.target === draggingId
        );

        if (!edgeExists) {
          const targetDef = ALL_NODE_DEFINITIONS[targetNode.type || ''];
          const outputs = targetDef?.outputs || [{ name: 'default' }];
          const sourceHandle = outputs[0]?.name || 'default';

          const inputs = draggingDef?.inputs || [{ name: 'default' }];
          const targetHandle = inputs[0]?.name || 'default';

          const newEdge: Edge = {
            id: `e${targetNode.id}-${draggingId}-${Date.now()}`,
            source: targetNode.id,
            target: draggingId,
            sourceHandle,
            targetHandle,
            type: 'custom',
          };

          setEdges([...edges, newEdge]);
          toast.success(`Connected ${targetNode.data?.label || targetNode.type} to ${node.data?.label || node.type}`);
        }
      }
    }
    // Handle drop on edge - insert dragged node into the edge
    else if (targetEdgeId) {
      const targetEdge = edges.find(e => e.id === targetEdgeId);
      if (targetEdge) {
        const sourceNode = nodes.find(n => n.id === targetEdge.source);
        const edgeTargetNode = nodes.find(n => n.id === targetEdge.target);

        if (sourceNode && edgeTargetNode) {
          const sourceWidth = sourceNode.width || 96;
          const sourceHeight = sourceNode.height || 96;
          const draggedWidth = node.width || 96;
          const draggedHeight = node.height || 96;
          const gap = 100; // 1 node width spacing

          // Calculate proper position for dragged node: 1 gap to the right of source
          const insertPosition = {
            x: sourceNode.position.x + sourceWidth + gap,
            y: sourceNode.position.y + (sourceHeight - draggedHeight) / 2,
          };

          // Calculate how much to shift downstream nodes
          const requiredSpaceEnd = insertPosition.x + draggedWidth + gap;
          const currentTargetX = edgeTargetNode.position.x;
          const shiftAmount = Math.max(0, requiredSpaceEnd - currentTargetX);

          // Build updated nodes: reposition dragged node and shift downstream
          const updatedNodes = nodes.map(n => {
            // Reposition the dragged node
            if (n.id === draggingId) {
              return { ...n, position: insertPosition };
            }
            // Don't shift the source node
            if (n.id === sourceNode.id) return n;
            // Shift downstream nodes
            if (shiftAmount > 0 && n.position.x >= currentTargetX - 10) {
              return { ...n, position: { ...n.position, x: n.position.x + shiftAmount } };
            }
            return n;
          });

          const originalSourceHandle = targetEdge.sourceHandle || 'default';
          const originalTargetHandle = targetEdge.targetHandle || 'default';

          const draggedInputs = draggingDef?.inputs || [{ name: 'default' }];
          const draggedOutputs = draggingDef?.outputs || [{ name: 'default' }];
          const draggedInputHandle = draggedInputs[0]?.name || 'default';
          const draggedOutputHandle = draggedOutputs[0]?.name || 'default';

          const edge1: Edge = {
            id: `e${sourceNode.id}-${draggingId}-${Date.now()}`,
            source: sourceNode.id,
            target: draggingId,
            sourceHandle: originalSourceHandle,
            targetHandle: draggedInputHandle,
            type: 'custom',
          };

          const edge2: Edge = {
            id: `e${draggingId}-${edgeTargetNode.id}-${Date.now() + 1}`,
            source: draggingId,
            target: edgeTargetNode.id,
            sourceHandle: draggedOutputHandle,
            targetHandle: originalTargetHandle,
            type: 'custom',
          };

          // Single batch update
          setNodes(updatedNodes);
          setEdges([...edges.filter(e => e.id !== targetEdge.id), edge1, edge2]);
          toast.success(`Inserted ${node.data?.label || node.type} between ${sourceNode.data?.label || sourceNode.type} and ${edgeTargetNode.data?.label || edgeTargetNode.type}`);
        }
      }
    }
  }, [nodes, edges, highlightedNodeId, highlightedEdgeId, setHighlightedNodeId, setHighlightedEdgeId, setNodes, setEdges]);

  // Handle node selection from anchor menu (now via palette)
  const handleAnchorMenuSelect = useCallback((nodeType: string) => {
    if (!anchorMenuState) return;

    const { sourceNodeId, sourceHandle, anchorType } = anchorMenuState;

    // Get the node definition to validate compatibility
    const targetDef = ALL_NODE_DEFINITIONS[nodeType];

    // VALIDATION: Ensure the selected node is compatible with the anchor type
    if (anchorType === 'chatModel') {
      if (targetDef?.subNodeConfig?.category !== 'chatModel') {
        toast.error('Invalid Selection', {
          description: 'Only chat model nodes can connect to the Model anchor. Try using Claude Chat Model or OpenAI Chat Model.',
        });
        setAnchorMenuState(null);
        setVisiblePaletteCategories(undefined);
        return;
      }
    } else if (anchorType === 'memory') {
      if (targetDef?.subNodeConfig?.category !== 'memory') {
        toast.error('Invalid Selection', {
          description: 'Only memory nodes can connect to the Memory anchor. Try using Simple Memory or LangChain Chat Memory.',
        });
        setAnchorMenuState(null);
        setVisiblePaletteCategories(undefined);
        return;
      }
    } else if (anchorType === 'tools' || anchorType === 'tool') {
      if (!targetDef?.toolCapable?.enabled) {
        toast.error('Invalid Selection', {
          description: 'Only tool-capable nodes can connect to the Tools anchor. Try using HTTP Request, Code Execution, or other tool nodes.',
        });
        setAnchorMenuState(null);
        setVisiblePaletteCategories(undefined);
        return;
      }
    } else if (anchorType === 'parser') {
      if (targetDef?.subNodeConfig?.category !== 'parser') {
        toast.error('Invalid Selection', {
          description: 'Only parser nodes can connect to the Parser anchor. Try using Agent Output Parser.',
        });
        setAnchorMenuState(null);
        setVisiblePaletteCategories(undefined);
        return;
      }
    } else if (anchorType === 'pollSource') {
      if (targetDef?.subNodeConfig?.category !== 'pollSource') {
        toast.error('Invalid Selection', {
          description: 'Only data source nodes can connect to the Poll Source anchor.',
        });
        setAnchorMenuState(null);
        setVisiblePaletteCategories(undefined);
        return;
      }
    }

    // Close the menu and reset categories
    setAnchorMenuState(null);
    setVisiblePaletteCategories(undefined);

    // Get the source node to calculate position for new node
    const sourceNode = nodes.find(n => n.id === sourceNodeId);
    if (!sourceNode) return;

    // Calculate position for new node (below the source node)
    const newNodePosition = {
      x: sourceNode.position.x,
      y: sourceNode.position.y + 150, // Position below the source node
    };

    // Get the node definition to use the proper display name
    const nodeDef = getNodeDefinition(nodeType);
    const baseDisplayName = nodeDef?.name || nodeDef?.label || nodeType;

    // Generate unique label to prevent duplicates
    const existingLabels = getNodeLabels(nodes);
    const uniqueLabel = generateUniqueNodeLabel(baseDisplayName, existingLabels);

    // Get default properties from node defaults
    const defaultProperties = getNodeDefaults(nodeType);

    // Create the new node
    const newNode: Node = {
      id: `${nodeType}-${Date.now()}`,
      type: nodeType,
      position: newNodePosition,
      data: {
        label: uniqueLabel,
        properties: defaultProperties,
      },
    };

    // Determine the correct target handle based on node type
    // Sub-nodes (chat models, memory) use 'subNodeInput', others use 'default'
    const targetHandle = nodeDef?.isSubNode ? 'subNodeInput' : 'default';

    // Create the edge connecting source to new node
    const newEdge: Edge = {
      id: `${sourceNodeId}-${newNode.id}`,
      source: sourceNodeId,
      sourceHandle: sourceHandle,
      target: newNode.id,
      targetHandle: targetHandle,
      type: 'custom',
    };

    // Add both node and edge
    setNodes((nds) => [...nds, newNode]);
    setEdges((eds) => [...eds, newEdge]);

    toast.success(`Added ${uniqueLabel}`);
  }, [anchorMenuState, nodes, setNodes, setEdges]);

  // Handle adding node from palette click
  const handleAddNode = useCallback(
    (type: string) => {
      // If we're adding from an anchor button, use the anchor menu select handler
      if (anchorMenuState) {
        handleAnchorMenuSelect(type);
        return;
      }
      
      // If we're adding after a specific node (from anchor or output handle)
      if (addingAfterNodeId) {
        const sourceNode = nodes.find(n => n.id === addingAfterNodeId);
        if (!sourceNode) return;

        // Get node definition
        const nodeDefinition = nodeDefinitions[type];

        // Determine node size based on category (complex nodes are larger)
        const isComplexNode = nodeDefinition?.category === 'triggers' || 
                             (nodeDefinition?.category as string) === 'flow-control';
        const nodeWidth = isComplexNode ? 128 : 96;
        const nodeHeight = isComplexNode ? 128 : 96;

        // Position the new node to the right of the source node
        const newPosition = {
          x: sourceNode.position.x + 96 + 150, // Node width + spacing
          y: sourceNode.position.y,
        };

        // Generate unique label to prevent duplicates
        const baseLabel = nodeDefinition?.label || type.charAt(0).toUpperCase() + type.slice(1);
        const existingLabels = getNodeLabels(nodes);
        const uniqueLabel = generateUniqueNodeLabel(baseLabel, existingLabels);

        // Create new node
        const newNode: Node = {
          id: `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type,
          position: newPosition,
          data: {
            label: uniqueLabel,
            category: nodeDefinition?.category || 'action',
            nodeType: type,
            parameters: {},
          },
          width: nodeWidth,
          height: nodeHeight,
        };

        // Create edge from source to new node
        const newEdge: Edge = {
          id: `${addingAfterNodeId}-${newNode.id}`,
          source: addingAfterNodeId,
          sourceHandle: addingAfterHandle, // Use the stored source handle
          target: newNode.id,
          type: 'custom',
        };

        setNodes([...nodes, newNode]);
        setEdges([...edges, newEdge]);
        setAddingAfterNodeId(null);
        setAddingAfterHandle(undefined);
        setSubNodeFilter(undefined); // Clear sub-node filter

        toast.success(`Added ${uniqueLabel} node`);
        return;
      }

      // If we're in insert mode, insert the node between the two connected nodes
      if (isInsertingNode && insertEdgeInfo) {
        const { edgeId, sourceId, targetId, midpoint } = insertEdgeInfo;
        
        // Get node definition
        const nodeDefinition = nodeDefinitions[type];
        
        // Find source and target nodes
        const sourceNode = nodes.find(n => n.id === sourceId);
        const targetNode = nodes.find(n => n.id === targetId);
        
        if (!sourceNode || !targetNode) return;
        
        // Calculate the spacing needed (node width + padding)
        const nodeWidth = 96; // Updated for icon-only nodes
        const spacing = 100; // Space between nodes
        const totalSpaceNeeded = nodeWidth + spacing * 2;
        
        // Calculate if nodes are horizontal or vertical
        const dx = targetNode.position.x - sourceNode.position.x;
        const dy = targetNode.position.y - sourceNode.position.y;
        const isHorizontal = Math.abs(dx) > Math.abs(dy);

        // Generate unique label to prevent duplicates
        const baseLabel = nodeDefinition?.label || type.charAt(0).toUpperCase() + type.slice(1);
        const existingLabels = getNodeLabels(nodes);
        const uniqueLabel = generateUniqueNodeLabel(baseLabel, existingLabels);

        // Create new node at the midpoint
        const newNode: Node = {
          id: `${type}-${Date.now()}`,
          type,
          position: midpoint,
          data: {
            label: uniqueLabel,
            category: nodeDefinition?.category || 'action',
            nodeType: type,
            parameters: {},
          },
        };
        
        // Reposition nodes to make space
        // Calculate the insertion point (midpoint between source and target)
        const insertX = midpoint.x;
        const insertY = midpoint.y;
        
        const updatedNodes = nodes.map(node => {
          // Skip the source node
          if (node.id === sourceId) {
            return node;
          }
          
          // Move all nodes that are to the right (horizontal) or below (vertical) the insertion point
          if (isHorizontal) {
            // Horizontal layout - move all nodes to the right of the insertion point
            if (node.position.x >= insertX) {
              return {
                ...node,
                position: {
                  x: node.position.x + totalSpaceNeeded / 2,
                  y: node.position.y,
                },
              };
            }
          } else {
            // Vertical layout - move all nodes below the insertion point
            if (node.position.y >= insertY) {
              return {
                ...node,
                position: {
                  x: node.position.x,
                  y: node.position.y + totalSpaceNeeded / 2,
                },
              };
            }
          }
          
          return node;
        });
        
        // Remove the original edge
        const updatedEdges = edges.filter(edge => edge.id !== edgeId);
        
        // Add two new edges: source -> newNode and newNode -> target
        const newEdges: Edge[] = [
          ...updatedEdges,
          {
            id: `${sourceId}-${newNode.id}`,
            source: sourceId,
            target: newNode.id,
            type: 'custom',
          },
          {
            id: `${newNode.id}-${targetId}`,
            source: newNode.id,
            target: targetId,
            type: 'custom',
          },
        ];
        
        setNodes([...updatedNodes, newNode]);
        setEdges(newEdges);
        setIsInsertingNode(false);
        setInsertEdgeInfo(null);
        
        toast.success(`Inserted ${nodeDefinition?.label || type} node`);
        return;
      }
      
      // Normal add node behavior
      const viewport = reactFlowInstance.getViewport();
      const centerX = (window.innerWidth / 2 - viewport.x) / viewport.zoom;
      const centerY = (window.innerHeight / 2 - viewport.y) / viewport.zoom;

      // Get node definition to include category and other metadata
      const nodeDefinition = nodeDefinitions[type];

      // Determine node size based on category (complex nodes are larger)
      const isComplexNode = nodeDefinition?.category === 'triggers' ||
                           (nodeDefinition?.category as string) === 'flow-control';
      const nodeWidth = isComplexNode ? 128 : 96;
      const nodeHeight = isComplexNode ? 128 : 96;

      // Position trigger nodes on the left side when canvas is empty
      // This provides a natural starting point for workflow building
      let position: { x: number; y: number };
      if (nodeDefinition?.category === 'triggers' && nodes.length === 0) {
        // Calculate left position (about 150px from left edge of visible canvas)
        const leftX = (150 - viewport.x) / viewport.zoom;
        // Center vertically, accounting for node height
        position = { x: leftX, y: centerY - nodeHeight / 2 };
      } else {
        // Default: center the node in viewport
        position = { x: centerX - nodeWidth / 2, y: centerY - nodeHeight / 2 };
      }
      
      // Get default properties from node defaults
      const defaultProperties = getNodeDefaults(type);

      // Generate unique label to prevent duplicates
      const baseLabel = nodeDefinition?.label || type.charAt(0).toUpperCase() + type.slice(1);
      const existingLabels = getNodeLabels(nodes);
      const uniqueLabel = generateUniqueNodeLabel(baseLabel, existingLabels);

      const newNode: Node = {
        id: `${type}-${Date.now()}`,
        type,
        position,
        data: {
          label: uniqueLabel,
          category: nodeDefinition?.category || 'action',
          nodeType: type,
          parameters: defaultProperties,
        },
        width: nodeWidth,
        height: nodeHeight,
      };

      setNodes([...nodes, newNode]);
      toast.success(`Added ${uniqueLabel} node`);
    },
    [reactFlowInstance, nodes, setNodes, edges, setEdges, isInsertingNode, insertEdgeInfo, addingAfterNodeId, addingAfterHandle, anchorMenuState, handleAnchorMenuSelect, nodeDefinitions]
  );

  // Handle edge delete
  // Use functional update to avoid dependency on edges array
  const handleEdgeDelete = useCallback((edgeId: string) => {
    setEdges((currentEdges) => currentEdges.filter(edge => edge.id !== edgeId));
    toast.success('Connection deleted');
  }, [setEdges]);

  // Handle edge add (insert node between)
  // Use functional update to avoid dependency on edges array
  const handleEdgeAdd = useCallback((edgeId: string, positions: { sourceX: number; sourceY: number; targetX: number; targetY: number }) => {
    // Use the store's edges directly via get() or pass edge info through data
    const currentEdges = useWorkflowStore.getState().edges;
    const edge = currentEdges.find(e => e.id === edgeId);
    if (!edge) return;
    
    // Calculate midpoint
    const midpoint = {
      x: (positions.sourceX + positions.targetX) / 2 - 48, // Center the node (96px wide)
      y: (positions.sourceY + positions.targetY) / 2 - 48, // Center the node (96px tall)
    };
    
    // Store the edge info and enter insert mode
    setInsertEdgeInfo({
      edgeId,
      sourceId: edge.source,
      targetId: edge.target,
      midpoint,
    });
    setIsInsertingNode(true);
    
    // Expand the palette to show all categories
    setPaletteCategory('categories');
    
    toast.info('Select a node from the palette to insert');
  }, []);

  // Enrich nodes with execution state
  const enrichedNodes = useMemo(() => {
    return nodes.map(node => {
      // Get execution status from real-time tracking
      const executionStatus = nodeExecutionStatuses[node.id] || 'idle';
      const isExecuting = executionStatus === 'running';
      
      return {
        ...node,
        data: {
          ...node.data,
          isExecuting,
          executionStatus,
        },
      };
    });
  }, [nodes, nodeExecutionStatuses]);

  // Enrich edges with custom data for delete and add handlers (no add edges)
  const enrichedEdges = useMemo(() => {
    return edges.map(edge => {
      const executionStatus = edgeExecutionStatuses[edge.id] || 'idle';
      return {
        ...edge,
        type: edge.type || 'custom',
        data: {
          ...edge.data,
          onDelete: handleEdgeDelete,
          onAdd: handleEdgeAdd,
          executionStatus, // 'idle' | 'active' | 'traversed'
        },
      };
    });
  }, [edges, handleEdgeDelete, handleEdgeAdd, edgeExecutionStatuses]);

  // Handle opening chat test panel
  const handleOpenChatTest = useCallback((nodeId: string) => {
    setSelectedChatTriggerId(nodeId);
    setIsChatTestOpen(true);
  }, []);

  // Handle add node click from add node button
  const handleAddNodeClick = useCallback((sourceNodeId: string, sourceHandle?: string, anchorType?: 'chatModel' | 'memory' | 'tools' | 'parser' | 'tool' | 'pollSource', anchorPosition?: { x: number; y: number }) => {
    // If anchor type is specified, open palette with that anchor category
    if (anchorType && anchorPosition) {
      // Store the anchor state for later connection
      setAnchorMenuState({
        visible: true,
        anchorType,
        position: anchorPosition,
        sourceNodeId,
        sourceHandle: sourceHandle || 'default',
      });

      // Map anchor type to category
      const categoryMap: Record<string, string> = {
        chatModel: 'anchor-chatModels',
        memory: 'anchor-memory',
        tools: 'anchor-tools',
        tool: 'anchor-tools',
        parser: 'anchor-parser',
        pollSource: 'anchor-pollSource', // For polling trigger - show pollable nodes
      };

      const anchorCategory = categoryMap[anchorType];

      // Only show the anchor category - prevents users from selecting incompatible nodes
      setVisiblePaletteCategories([anchorCategory]);

      // Open the palette to the anchor category
      setPaletteCategory(anchorCategory);
      setAddingAfterNodeId(sourceNodeId);
      setAddingAfterHandle(sourceHandle);

      return;
    }
    
    // For regular add node (no anchor type), open the palette normally
    setAddingAfterNodeId(sourceNodeId);
    setAddingAfterHandle(sourceHandle);
    setSubNodeFilter(undefined);
    setVisiblePaletteCategories(undefined); // Show all default categories
    setPaletteCategory('categories');
    toast.info('Select a node from the palette to add');
  }, []);

  // Handle palette close - hide anchor categories
  const handlePaletteClose = useCallback(() => {
    setVisiblePaletteCategories(undefined);
    setAnchorMenuState(null);
  }, []);

  // Use enriched nodes/edges directly
  const allNodes = enrichedNodes;
  const allEdges = enrichedEdges;

  // Enable keyboard shortcuts
  useKeyboardShortcuts([
    {
      key: 's',
      ctrl: true,
      description: 'Save workflow',
      action: handleSave,
    },
    {
      key: 'Enter',
      ctrl: true,
      description: 'Execute workflow',
      action: handleExecute,
    },
  ]);

  if (loading) {
    return <WorkflowEditorSkeleton />;
  }

  if (!workflow) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Workflow not found</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Validation Errors */}
      <ValidationErrors
        errors={validationErrors}
        warnings={validationWarnings}
        onClose={() => {
          setValidationErrors([]);
          setValidationWarnings([]);
        }}
        onNodeClick={(nodeId) => {
          // Focus on the node with error
          const node = nodes.find((n) => n.id === nodeId);
          if (node) {
            setCenter(node.position.x + 100, node.position.y + 50, { zoom: 1.5, duration: 800 });
          }
        }}
      />

      {/* Loading Overlay for Saving */}
      <LoadingOverlay isLoading={saving} message="Saving workflow..." />

      {/* Header */}
      <div className="h-14 border-b bg-background px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
            const returnFolder = searchParams.get('returnFolder');
            const teamFolder = searchParams.get('teamFolder');
            const teamId = searchParams.get('teamId');
            const activeTab = searchParams.get('activeTab');
            const returnTo = searchParams.get('returnTo');
            const page = searchParams.get('page');
            const pageParam = page ? `&page=${page}` : '';

            let destination;
            if (returnTo === 'executions') {
              // Return to executions page
              destination = `/executions${page ? `?page=${page}` : ''}`;
            } else if (teamFolder) {
              // Return to team folder view in WorkflowList
              destination = `/workflows?teamFolder=${teamFolder}${pageParam}`;
            } else if (teamId) {
              // Return to team details page
              const tabParam = activeTab ? `?activeTab=${activeTab}` : '';
              destination = `/teams/${teamId}${tabParam}`;
            } else if (returnFolder) {
              // Return to personal folder
              destination = `/workflows?folder=${returnFolder}${pageParam}`;
            } else {
              // Return to my workflows
              destination = `/workflows${page ? `?page=${page}` : ''}`;
            }

            guardedNavigate(destination);
          }}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <EditableWorkflowHeader
            name={workflow.name as string}
            description={workflow.description as string}
            workflowId={id}
            onNameChange={handleNameChange}
            onDescriptionChange={handleDescriptionChange}
            onVersionRestore={handleLoadVersion}
            versionRefreshTrigger={versionRefreshTrigger}
            currentVersionNumber={currentVersionNumber}
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 border rounded-md bg-background">
            <span className="text-sm text-muted-foreground">
              {workflow.active ? 'Active' : 'Inactive'}
            </span>
            <Switch
              checked={workflow.active as boolean}
              onCheckedChange={handleToggleActive}
              title={workflow.active ? 'Deactivate workflow' : 'Activate workflow'}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowShareModal(true)}
            title="Share Workflow"
          >
            <Share2 className="mr-2 h-4 w-4" />
            Share
          </Button>
          {isWorkflowAssistantEnabled && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAssistantOpen(true)}
              title="AI Workflow Assistant (Ctrl+Shift+A)"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Assistant
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleSave} disabled={saving}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button 
            size="sm" 
            onClick={handleExecute}
            disabled={currentExecution?.status === 'running'}
            className={cn(
              currentExecution?.status === 'running' && 'animate-pulse bg-blue-600 hover:bg-blue-600'
            )}
          >
            <Play className={cn(
              "mr-2 h-4 w-4",
              currentExecution?.status === 'running' && 'animate-spin'
            )} />
            {currentExecution?.status === 'running' ? 'Running...' : 'Run'}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Workflow Assistant Panel - Inline on left */}
        {isWorkflowAssistantEnabled && isAssistantOpen && (
          <div className="w-[400px] border-r bg-background flex-shrink-0 flex flex-col">
            <WorkflowAssistantPanel
              workflowId={id!}
              workflowName={workflow?.name as string}
              nodes={nodes}
              edges={edges}
              onWorkflowPatch={(patch: WorkflowPatch) => {
                // Apply workflow patch from assistant
                if (patch.addNodes?.length) {
                  const newNodes = patch.addNodes.map(op => ({
                    id: op.id,
                    type: op.type,
                    position: op.position,
                    data: {
                      label: op.label || op.type,
                      parameters: op.parameters || {},
                    },
                  }));
                  setNodes([...nodes, ...newNodes]);
                }
                if (patch.updateNodes?.length) {
                  setNodes(nodes.map(n => {
                    const update = patch.updateNodes?.find(u => u.nodeId === n.id);
                    if (update) {
                      return {
                        ...n,
                        data: {
                          ...n.data,
                          label: update.label || n.data?.label,
                          parameters: {
                            ...n.data?.parameters,
                            ...update.parameters,
                          },
                        },
                      };
                    }
                    return n;
                  }));
                }
                if (patch.deleteNodes?.length) {
                  setNodes(nodes.filter(n => !patch.deleteNodes?.includes(n.id)));
                  // Also remove edges connected to deleted nodes
                  setEdges(edges.filter(e =>
                    !patch.deleteNodes?.includes(e.source) &&
                    !patch.deleteNodes?.includes(e.target)
                  ));
                }
                if (patch.addEdges?.length) {
                  const newEdges = patch.addEdges.map(op => ({
                    id: op.id || `edge_${Date.now()}`,
                    source: op.source,
                    target: op.target,
                    sourceHandle: op.sourceHandle,
                    targetHandle: op.targetHandle,
                    type: 'custom',
                  }));
                  setEdges([...edges, ...newEdges]);
                }
                if (patch.deleteEdges?.length) {
                  setEdges(edges.filter(e => !patch.deleteEdges?.includes(e.id)));
                }
                toast.success('Workflow updated by assistant');
              }}
              onClose={() => setIsAssistantOpen(false)}
            />
          </div>
        )}

        {/* Canvas and Execution Panel */}
        <LiveExecutionProvider currentExecution={currentExecution}>
        <div className="flex-1 flex flex-col min-h-0 relative">
          {isExecutionPanelOpen ? (
            <PanelGroup direction="vertical">
              {/* Canvas Panel */}
              <ResizablePanel defaultSize={60} minSize={30}>
                <div className="h-full relative">
                  <WorkflowProvider value={{ onAddNodeClick: handleAddNodeClick, onOpenChatTest: handleOpenChatTest }}>
                    <CanvasContextMenu
                      canvasMenuOpen={canvasMenuOpen}
                      canvasMenuPosition={canvasMenuPosition}
                      onCanvasMenuOpenChange={setCanvasMenuOpen}
                    >
                      <div className="relative w-full h-full">
                        <ReactFlow
                        nodes={allNodes}
                        edges={allEdges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        isValidConnection={isValidConnection}
                        onNodeClick={onNodeClick}
                        onNodeDoubleClick={onNodeDoubleClick}
                        onNodeContextMenu={onNodeContextMenu}
                        onEdgeClick={onEdgeClick}
                        onEdgeContextMenu={onEdgeContextMenu}
                        onPaneClick={onPaneClick}
                        onPaneContextMenu={onPaneContextMenu}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        nodesConnectable={true}
                        connectOnClick={false}
                        selectionOnDrag={true}
                        panOnDrag={[1, 2]}
                        selectionMode={SelectionMode.Partial}
                        elevateEdgesOnSelect={false}
                        defaultEdgeOptions={{
                          type: 'custom',
                          animated: false,
                          style: { strokeWidth: 2, stroke: '#94a3b8' },
                        }}
                        connectionLineType={ConnectionLineType.Bezier}
                        connectionLineStyle={{ 
                          strokeWidth: 2,
                          stroke: '#94a3b8',
                          strokeDasharray: '5,5',
                        }}
                        defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
                        minZoom={0.1}
                        maxZoom={2}
                        className="workflow-canvas"
                        // Performance optimizations to reduce event listeners
                        deleteKeyCode={null}
                        multiSelectionKeyCode="Shift"
                        zoomActivationKeyCode="Meta"
                        panActivationKeyCode="Space"
                        // Reduce re-renders
                        fitViewOptions={{ padding: 0.2 }}
                        // Prevent unnecessary listener creation
                        proOptions={{ hideAttribution: true }}
                      >
                      <Background 
                        gap={15}
                        size={1}
                        color="hsl(var(--border))"
                        variant={BackgroundVariant.Dots}
                      />
                      <Controls 
                        position="top-left"
                        className="!top-4 !left-4"
                        showZoom={true}
                        showFitView={false}
                        showInteractive={true}
                      >
                        <ControlButton
                          onClick={handleFitView}
                          title="Fit View (Ctrl+0)"
                        >
                          <Maximize2 className="h-4 w-4" />
                        </ControlButton>
                      </Controls>
                      <MiniMap 
                        position="bottom-right"
                        className="!bottom-8 !right-4"
                        zoomable
                        pannable
                        nodeColor={(node) => {
                          const nodeType = node.type || 'default';
                          const colors: Record<string, string> = {
                            webhook: '#f97316',
                            schedule: '#f97316',
                            http: '#3b82f6',
                            email: '#3b82f6',
                            database: '#10b981',
                            transform: '#10b981',
                            conditional: '#eab308',
                            loop: '#eab308',
                            code: '#8b5cf6',
                            aiAgent: '#a855f7',
                          };
                          return colors[nodeType] || '#6b7280';
                        }}
                        maskColor="rgba(0, 0, 0, 0.1)"
                        style={{ cursor: 'pointer' }}
                      />
                      <Panel position="top-right" className="bg-background border rounded-lg p-2">
                        <div className="text-sm text-muted-foreground">
                          {nodes.length} nodes, {edges.length} connections
                        </div>
                      </Panel>
                      {allNodes.length === 0 && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                          <div className="pointer-events-auto">
                            <div className="flex flex-col items-center justify-center text-center p-8 rounded-lg border-2 border-dashed bg-background/95 backdrop-blur-sm shadow-lg w-96">
                              <div className="rounded-full bg-primary/10 p-4 mb-4">
                                <Zap className="h-8 w-8 text-primary" />
                              </div>
                              <h3 className="text-lg font-semibold mb-2">Start with a Trigger</h3>
                              <p className="text-sm text-muted-foreground mb-4">
                                Every workflow needs a trigger to start. Click below to browse trigger nodes.
                              </p>
                              <Button onClick={() => setPaletteCategory('triggers')} size="lg">
                                <Zap className="mr-2 h-4 w-4" />
                                Add Trigger Node
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                        </ReactFlow>
                      </div>
                    </CanvasContextMenu>
                  </WorkflowProvider>
                </div>
              </ResizablePanel>

              {/* Resize Handle */}
              <PanelResizeHandle className="h-1 bg-border hover:bg-primary/20 transition-colors" />

              {/* Execution Panel */}
              <ResizablePanel defaultSize={40} minSize={20} maxSize={70}>
                <LiveExecutionPanel
                  isOpen={isExecutionPanelOpen}
                  onToggle={() => setIsExecutionPanelOpen(!isExecutionPanelOpen)}
                  executionProgress={executionProgress}
                />
              </ResizablePanel>
            </PanelGroup>
          ) : (
            <>
              {/* Canvas when panel is closed - full height since panel is now floating */}
              <div className="flex-1 relative overflow-hidden">
                <WorkflowProvider value={{ onAddNodeClick: handleAddNodeClick, onOpenChatTest: handleOpenChatTest }}>
                  <CanvasContextMenu
                    canvasMenuOpen={canvasMenuOpen}
                    canvasMenuPosition={canvasMenuPosition}
                    onCanvasMenuOpenChange={setCanvasMenuOpen}
                  >
                    <div className="relative w-full h-full">
                      <ReactFlow
                      nodes={allNodes}
                      edges={allEdges}
                      onNodesChange={onNodesChange}
                      onEdgesChange={onEdgesChange}
                      onConnect={onConnect}
                      isValidConnection={isValidConnection}
                      onNodeClick={onNodeClick}
                      onNodeDoubleClick={onNodeDoubleClick}
                      onNodeContextMenu={onNodeContextMenu}
                      onEdgeClick={onEdgeClick}
                      onEdgeContextMenu={onEdgeContextMenu}
                      onPaneClick={onPaneClick}
                      onPaneContextMenu={onPaneContextMenu}
                      onDrop={onDrop}
                      onDragOver={onDragOver}
                      onNodeDragStart={onNodeDragStart}
                      onNodeDrag={onNodeDrag}
                      onNodeDragStop={onNodeDragStop}
                      nodeTypes={nodeTypes}
                      edgeTypes={edgeTypes}
                      nodesConnectable={true}
                      connectOnClick={false}
                      selectionOnDrag={true}
                      panOnDrag={[1, 2]}
                      selectionMode={SelectionMode.Partial}
                      elevateEdgesOnSelect={false}
                      defaultEdgeOptions={{
                        type: 'custom',
                        animated: false,
                        style: { strokeWidth: 2 },
                      }}
                      connectionLineType={ConnectionLineType.SmoothStep}
                      connectionLineStyle={{ strokeWidth: 2 }}
                      defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
                      minZoom={0.1}
                      maxZoom={2}
                      className="workflow-canvas"
                      // Performance optimizations to reduce event listeners
                      deleteKeyCode={null}
                      multiSelectionKeyCode="Shift"
                      zoomActivationKeyCode="Meta"
                      panActivationKeyCode="Space"
                      // Reduce re-renders
                      fitViewOptions={{ padding: 0.2 }}
                      // Prevent unnecessary listener creation
                      proOptions={{ hideAttribution: true }}
                    >
                    <Background 
                      gap={15}
                      size={1}
                      color="hsl(var(--border))"
                      variant={BackgroundVariant.Dots}
                    />
                    <Controls 
                      position="top-left"
                      className="!top-4 !left-4"
                      showZoom={true}
                      showFitView={true}
                      showInteractive={true}
                    />
                    <MiniMap 
                      position="bottom-right"
                      className="!bottom-20 !right-4"
                      zoomable
                      pannable
                      nodeColor={(node) => {
                        const nodeType = node.type || 'default';
                        const colors: Record<string, string> = {
                          webhook: '#f97316',
                          schedule: '#f97316',
                          http: '#3b82f6',
                          email: '#3b82f6',
                          database: '#10b981',
                          transform: '#10b981',
                          conditional: '#eab308',
                          loop: '#eab308',
                          code: '#8b5cf6',
                          aiAgent: '#a855f7',
                        };
                        return colors[nodeType] || '#6b7280';
                      }}
                      maskColor="rgba(0, 0, 0, 0.1)"
                      style={{ cursor: 'pointer' }}
                    />
                    <Panel position="top-right" className="bg-background border rounded-lg p-2">
                      <div className="text-sm text-muted-foreground">
                        {nodes.length} nodes, {edges.length} connections
                      </div>
                    </Panel>
                    {allNodes.length === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                        <div className="pointer-events-auto">
                          <div className="flex flex-col items-center justify-center text-center p-8 rounded-lg border-2 border-dashed bg-background/95 backdrop-blur-sm shadow-lg w-96">
                            <div className="rounded-full bg-primary/10 p-4 mb-4">
                              <Zap className="h-8 w-8 text-primary" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2">Start with a Trigger</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                              Every workflow needs a trigger to start. Click below to browse trigger nodes.
                            </p>
                            <Button onClick={() => setPaletteCategory('triggers')} size="lg">
                              <Zap className="mr-2 h-4 w-4" />
                              Add Trigger Node
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                      </ReactFlow>
                    </div>
                  </CanvasContextMenu>
                </WorkflowProvider>
              </div>

              {/* Collapsed Execution Panel - Always visible at bottom, only spans canvas width */}
              <LiveExecutionPanel
                isOpen={isExecutionPanelOpen}
                onToggle={() => setIsExecutionPanelOpen(!isExecutionPanelOpen)}
                executionProgress={executionProgress}
              />
            </>
          )}
        </div>
        </LiveExecutionProvider>

        {/* Node Palette */}
        <EnhancedNodePalette
          onAddNode={handleAddNode}
          initialCategory={paletteCategory}
          subNodeFilter={subNodeFilter}
          visibleCategories={visiblePaletteCategories}
          onClose={handlePaletteClose}
          forceClose={paletteForceCloseCounter}
        />
      </div>

      {/* Share Modal */}
      <ShareModal
        workflowId={id!}
        workflowName={workflow.name as string}
        open={showShareModal}
        onOpenChange={setShowShareModal}
      />

      {/* Chat Test Panel */}
      <Sheet open={isChatTestOpen} onOpenChange={setIsChatTestOpen}>
        <SheetContent side="right" className="w-[500px] sm:w-[500px] p-0">
          <SheetHeader className="sr-only">
            <SheetTitle>Test Chat Workflow</SheetTitle>
            <SheetDescription>
              Send test messages to your chat workflow
            </SheetDescription>
          </SheetHeader>
          {selectedChatTriggerId && (
            <ChatTestPanel
              workflowId={id!}
              chatTriggerNodeId={selectedChatTriggerId}
              onClose={() => setIsChatTestOpen(false)}
              isWorkflowActive={workflow?.active as boolean}
              workflowDefinition={{
                nodes,
                edges,
                name: workflow?.name,
              }}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Node Editor Modal */}
      <NodeEditorModal
        isOpen={isNodeEditorOpen}
        onClose={() => {
          setIsNodeEditorOpen(false);
          setEditingNodeId(null);
        }}
        nodeId={editingNodeId || ''}
        workflowId={id!}
      />

      {/* Keyboard Shortcuts Dialog */}
      <KeyboardShortcutsDialog
        open={showShortcutsDialog}
        onClose={() => setShowShortcutsDialog(false)}
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        isSaving={saving}
        onSave={async () => {
          // Capture path before save (save changes state which affects pendingPath)
          const pathToNavigate = pendingPath;
          await handleSave();
          setShowUnsavedDialog(false);
          // Navigate directly since hasUnsavedChanges is now false
          if (pathToNavigate === '__BACK__') {
            window.history.go(-2);
          } else if (pathToNavigate) {
            navigate(pathToNavigate);
          }
        }}
        onDiscard={() => {
          setShowUnsavedDialog(false);
          proceed();
        }}
        onCancel={() => {
          setShowUnsavedDialog(false);
          cancel();
        }}
      />

      {/* n8n Import Dialog */}
      {n8nImportState.workflow && (
        <N8nImportDialog
          open={n8nImportState.isOpen}
          onOpenChange={(open) => !open && closeN8nImportDialog()}
          n8nWorkflow={n8nImportState.workflow}
          onImport={(result: TransformResult) => {
            // Add the converted nodes and edges to the canvas
            const { nodes: newNodes, edges: newEdges } = result.workflow;

            // Add nodes with offset from current view
            newNodes.forEach((node) => {
              addNode({
                ...node,
                position: {
                  x: node.position.x + 100,
                  y: node.position.y + 100,
                },
              });
            });

            // Add edges
            newEdges.forEach((edge) => {
              const edgeWithType = {
                ...edge,
                type: 'custom',
              };
              storeOnConnect({
                source: edge.source,
                target: edge.target,
                sourceHandle: edge.sourceHandle || 'default',
                targetHandle: edge.targetHandle || 'default',
              });
            });

            toast.success(`Imported ${newNodes.length} nodes from n8n workflow`);
            closeN8nImportDialog();

            // Fit view to show all imported nodes after a short delay for ReactFlow to process
            setTimeout(() => {
              reactFlowFitView({
                padding: 0.2,
                duration: 400,
              });
            }, 100);
          }}
        />
      )}

      {/* Edge Context Menu */}
      <EdgeContextMenuPortal
        edgeId={contextMenuEdgeId}
        position={edgeMenuPosition}
        isOpen={edgeMenuOpen}
        onOpenChange={setEdgeMenuOpen}
      />
    </div>
  );
}

export default function WorkflowEditor() {
  return (
    <WorkflowErrorBoundary>
      <ReactFlowProvider>
        <ManualPasteProvider>
          <WorkflowEditorInner />
          <ManualPasteDialogWrapper />
        </ManualPasteProvider>
      </ReactFlowProvider>
    </WorkflowErrorBoundary>
  );
}

function ManualPasteDialogWrapper() {
  const { isDialogOpen, closeDialog, handlePaste } = useManualPaste();
  
  return (
    <ManualPasteDialog
      open={isDialogOpen}
      onOpenChange={closeDialog}
      onPaste={handlePaste}
    />
  );
}
